import {
  AimOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DeploymentUnitOutlined,
  DollarOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  EditOutlined,
  FolderOpenOutlined,
  FundOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MessageOutlined,
  PlusOutlined,
  RadarChartOutlined,
  RocketOutlined,
  SafetyOutlined,
  SearchOutlined,
  ShopOutlined,
  TeamOutlined,
  ThunderboltOutlined
} from "@ant-design/icons";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Progress,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import relativeTime from "dayjs/plugin/relativeTime";
import * as Nominatim from "nominatim-browser";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import ProjectDetails from "../components/Projects/ProjectDetails";
import ThreadDrawer from "../components/Projects/ThreadDrawer";
import {
  ProjectActionType,
  ProjectContextPayload,
  runProjectAction,
} from "../services/aiProjectService";
import {
  getAllPurchaseOrders,
  PurchaseOrder,
} from "../services/purchaseOrderService";
import {
  assignAccountManager,
  createProject,
  createRetailer,
  deleteProject,
  deleteRetailer,
  getRetailerEmails,
  getRetailerMailAlerts,
  getRetailers,
  Project,
  reassignRetailerCrm,
  Retailer,
  RetailerMailAlert,
  RetailerMailMessage,
  summarizeRetailerEmail,
  summarizeRetailerThread,
  updateProject,
  updateRetailer,
  updateRetailerEmailImportance
} from "../services/retailerApi";
import { getAllSuppliers, Supplier } from "../services/supplierApi";
import { getActiveUsers, User } from "../services/userService";
import { useAppDispatch, useAppSelector } from "../store";
import {
  clearHotMemory,
  HotMemoryItemType,
  removeHotMemoryItem,
  toggleHotMemoryPin,
} from "../store/hotMemorySlice";
import {
  buildGlobalRetailerKpis,
  buildRetailerKpisMap,
} from "../utils/KPIS";

dayjs.extend(relativeTime);
dayjs.locale("fr");

const { Title, Text } = Typography;

const EUROPE_GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const EUROPE_COUNTRIES = new Set([
  "France",
  "Germany",
  "Spain",
  "Italy",
  "United Kingdom",
  "Portugal",
  "Belgium",
  "Netherlands",
  "Luxembourg",
  "Switzerland",
  "Austria",
  "Ireland",
  "Norway",
  "Sweden",
  "Finland",
  "Denmark",
  "Poland",
  "Czechia",
  "Slovakia",
  "Hungary",
  "Romania",
  "Bulgaria",
  "Greece",
  "Croatia",
  "Slovenia",
  "Serbia",
  "Bosnia and Herzegovina",
  "North Macedonia",
  "Albania",
  "Montenegro",
  "Estonia",
  "Latvia",
  "Lithuania",
  "Ukraine",
  "Belarus",
  "Moldova",
]);
const EUROPE_COUNTRY_OPTIONS = Array.from(EUROPE_COUNTRIES)
  .sort()
  .map((country) => ({ value: country, label: country }));
const CITY_COORDINATES: Record<string, [number, number]> = {
  paris: [2.3522, 48.8566],
  lyon: [4.8357, 45.764],
  marseille: [5.3698, 43.2965],
  lille: [3.0573, 50.6292],
  toulouse: [1.4442, 43.6047],
  nantes: [-1.5536, 47.2184],
  bordeaux: [-0.5792, 44.8378],
  nice: [7.2619, 43.7102],
  strasbourg: [7.7521, 48.5734],
  biarritz: [-1.5596, 43.4832],
  stockholm: [18.0686, 59.3293],
  copenhagen: [12.5683, 55.6761],
  helsinki: [24.9384, 60.1699],
  oslo: [10.7522, 59.9139],
  dublin: [-6.2603, 53.3498],
  prague: [14.4378, 50.0755],
  budapest: [19.0402, 47.4979],
  athens: [23.7275, 37.9838],
  bucharest: [26.1025, 44.4268],
};
const COUNTRY_COORDINATES: Record<string, [number, number]> = {
  france: [2.3522, 48.8566],
  "united kingdom": [-0.1276, 51.5072],
  germany: [13.405, 52.52],
  italy: [12.4964, 41.9028],
  spain: [-3.7038, 40.4168],
  belgium: [4.3517, 50.8503],
  netherlands: [4.9041, 52.3676],
  portugal: [-9.1393, 38.7223],
  switzerland: [8.5417, 47.3769],
  austria: [16.3738, 48.2082],
  ireland: [-6.2603, 53.3498],
  poland: [21.0122, 52.2297],
  czechia: [14.4378, 50.0755],
  "czech republic": [14.4378, 50.0755],
  hungary: [19.0402, 47.4979],
  romania: [26.1025, 44.4268],
  greece: [23.7275, 37.9838],
  sweden: [18.0686, 59.3293],
  norway: [10.7522, 59.9139],
  finland: [24.9384, 60.1699],
  denmark: [12.5683, 55.6761],
};
const normalizeKey = (value?: string) => value?.trim().toLowerCase();
const DEFAULT_CRM_KEY = "store";
const CRM_TABS_STORAGE_KEY = "honnore.crmTabs.v1";
type CrmColumnConfig = {
  key: string;
  label: string;
  color: string;
  stage?: "lead" | "discovery" | "qualified" | "negotiation" | "contract" | "onboarding" | "launched";
};
type CrmTabConfig = {
  key: string;
  label: string;
  icon: string;
  validatorUserId?: string;
  validatorUserName?: string;
  columns: CrmColumnConfig[];
};
const DEFAULT_CRM_COLUMNS: CrmColumnConfig[] = [
  { key: "lead", label: "Prospect", color: "#94a3b8", stage: "lead" },
  { key: "discovery", label: "Cible", color: "#38bdf8", stage: "discovery" },
  { key: "qualified", label: "Contacté", color: "#818cf8", stage: "qualified" },
  { key: "negotiation", label: "Négociation", color: "#fbbf24", stage: "negotiation" },
  { key: "contract", label: "Contrat", color: "#f472b6", stage: "contract" },
  { key: "onboarding", label: "Onboarding", color: "#a78bfa", stage: "onboarding" },
  { key: "launched", label: "Lancé", color: "#10b981", stage: "launched" },
];
const DEFAULT_CRM_TABS: CrmTabConfig[] = [{
  key: DEFAULT_CRM_KEY,
  label: "Boutiques",
  icon: "team",
  columns: DEFAULT_CRM_COLUMNS,
}];
const CRM_ICON_OPTIONS = [
  { value: "team", label: "Équipe", Icon: TeamOutlined },
  { value: "shop", label: "Boutique", Icon: ShopOutlined },
  { value: "target", label: "Cible", Icon: AimOutlined },
  { value: "rocket", label: "Lancement", Icon: RocketOutlined },
  { value: "network", label: "Réseau", Icon: DeploymentUnitOutlined },
  { value: "location", label: "Localisation", Icon: EnvironmentOutlined },
  { value: "finance", label: "Finance", Icon: FundOutlined },
];
const getCrmIconComponent = (iconKey?: string) =>
  CRM_ICON_OPTIONS.find((option) => option.value === iconKey)?.Icon || TeamOutlined;
const normalizeCrmTabConfig = (tab: Partial<CrmTabConfig>): CrmTabConfig => ({
  key: String(tab.key || DEFAULT_CRM_KEY),
  label: String(tab.label || tab.key || "CRM"),
  icon: String(tab.icon || "team"),
  validatorUserId: tab.validatorUserId ? String(tab.validatorUserId) : undefined,
  validatorUserName: tab.validatorUserName ? String(tab.validatorUserName) : undefined,
  columns: Array.isArray(tab.columns) && tab.columns.length
    ? tab.columns.map((column) => ({
        key: String(column.key),
        label: String(column.label || column.key),
        color: String(column.color || "#94a3b8"),
        stage: column.stage,
      }))
    : DEFAULT_CRM_COLUMNS.map((column) => ({ ...column })),
});
const slugifyCrmKey = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "crm";
const ensureUniqueCrmKey = (base: string, existingKeys: string[]) => {
  const existing = new Set(existingKeys);
  let key = slugifyCrmKey(base);
  let index = 2;
  while (existing.has(key)) {
    key = `${slugifyCrmKey(base)}-${index}`;
    index += 1;
  }
  return key;
};
const buildAddressQuery = (location?: {
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}) =>
  [location?.street, location?.postalCode, location?.city, location?.country]
    .filter(Boolean)
    .join(", ");
const resolveCoordinates = (city?: string, country?: string) => {
  if (city) {
    const cityKey = normalizeKey(city);
    if (cityKey && CITY_COORDINATES[cityKey]) {
      return CITY_COORDINATES[cityKey];
    }
  }
  const countryKey = normalizeKey(country);
  if (countryKey && COUNTRY_COORDINATES[countryKey]) {
    return COUNTRY_COORDINATES[countryKey];
  }
  return undefined;
};
const jitterCoordinates = (coords: [number, number], seed: number) => {
  if (!seed) return coords;
  const angle = seed * 0.9;
  const radius = 0.18 + Math.floor(seed / 6) * 0.05;
  return [coords[0] + Math.cos(angle) * radius, coords[1] + Math.sin(angle) * radius] as [
    number,
    number,
  ];
};
const RETAILER_EXPORT_COLUMNS: Array<{
  key: string;
  label: string;
  defaultSelected: boolean;
  getValue: (retailer: Retailer) => string | number | undefined;
}> = [
  { key: "name", label: "Nom", defaultSelected: true, getValue: (r) => r.name },
  { key: "group", label: "Groupe", defaultSelected: false, getValue: (r) => r.group },
  { key: "segment", label: "Segment", defaultSelected: true, getValue: (r) => r.segment },
  { key: "status", label: "Statut", defaultSelected: true, getValue: (r) => r.status },
  {
    key: "onboardingStage",
    label: "Stage onboarding",
    defaultSelected: true,
    getValue: (r) => r.crm?.onboardingStage,
  },
  {
    key: "onboardingPriority",
    label: "Priorité onboarding",
    defaultSelected: false,
    getValue: (r) => r.crm?.onboardingPriority,
  },
  {
    key: "proposedBrand",
    label: "Marque à proposer",
    defaultSelected: false,
    getValue: (r) => r.crm?.proposedBrand,
  },
  {
    key: "estimatedValue",
    label: "Valeur estimée",
    defaultSelected: false,
    getValue: (r) => r.crm?.estimatedValue,
  },
  {
    key: "registrationNumber",
    label: "SIREN/SIRET",
    defaultSelected: false,
    getValue: (r) => r.kyc?.registrationNumber,
  },
  {
    key: "eshopUrl",
    label: "URL e-shop",
    defaultSelected: false,
    getValue: (r) => r.kyc?.eshopUrl,
  },
  {
    key: "countries",
    label: "Pays",
    defaultSelected: true,
    getValue: (r) => {
      const countries = new Set<string>();
      (r.channelsDetails || []).forEach((channel) => {
        channel.coverage?.countries?.forEach((country) => country && countries.add(country));
        if (channel.location?.country) countries.add(channel.location.country);
      });
      return Array.from(countries).join(", ");
    },
  },
  {
    key: "accountManager",
    label: "Account manager",
    defaultSelected: false,
    getValue: (r) =>
      [r.accountManager?.firstName, r.accountManager?.lastName]
        .filter(Boolean)
        .join(" "),
  },
  {
    key: "contacts",
    label: "Contacts",
    defaultSelected: false,
    getValue: (r) => (r.contacts || []).map((c) => c.name).filter(Boolean).join(", "),
  },
];
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value || 0);
const channelClass = (value?: string) => {
  if (value === "department_store") return "department";
  if (value === "pos") return "pos";
  return value || "retail";
};
const getRetailerCountries = (retailer: Retailer) => {
  const locations = new Set<string>();
  
  // Locations from KYC
  if (retailer.kyc) {
    if (retailer.kyc.marketScopeType === "cities" && retailer.kyc.marketCities?.length) {
      retailer.kyc.marketCities.forEach(c => locations.add(c));
    } else if (retailer.kyc.marketScopeType === "regions" && retailer.kyc.marketRegions?.length) {
      retailer.kyc.marketRegions.forEach(r => locations.add(r));
    } else if (retailer.kyc.marketScopeType === "countries" && retailer.kyc.marketCountries?.length) {
      retailer.kyc.marketCountries.forEach(c => locations.add(c));
    }
  }

  // Legacy locations from channels
  (retailer.channelsDetails || []).forEach((channel) => {
    channel.coverage?.countries?.forEach((country) => locations.add(country));
    if (channel.location?.country) locations.add(channel.location.country);
  });
  
  return Array.from(locations).filter(Boolean);
};

const getContactDisplayName = (retailer: Retailer, email?: string) => {
  if (!email) return "—";
  const normalized = email.toLowerCase();
  const contact = retailer.contacts?.find((item) => item.email?.toLowerCase() === normalized);
  if (contact?.name) return contact.name;
  return email.split("@")[0];
};
// Status colors for reference
// active: green
// paused: orange
// prospect: blue
// dormant: red

const toFormValues = (retailer: Retailer) => ({
  ...retailer,
  channelsDetails: (retailer.channelsDetails || []).map((channel) => ({
    ...channel,
    launchedAt: channel.launchedAt ? dayjs(channel.launchedAt) : undefined,
    stoppedAt: channel.stoppedAt ? dayjs(channel.stoppedAt) : undefined,
  })),
});

const normalizePayload = (values: any) => {
  const payload = { ...values };
  if (payload.channelsDetails) {
    payload.channelsDetails = payload.channelsDetails.map((channel: any) => ({
      ...channel,
      launchedAt: channel.launchedAt ? dayjs(channel.launchedAt).toISOString() : undefined,
      stoppedAt: channel.stoppedAt ? dayjs(channel.stoppedAt).toISOString() : undefined,
    }));
  }

  const mode = payload.logistics?.fulfillmentMode;
  if (mode === "hub") {
    payload.logistics = { ...payload.logistics, hubs: [] };
    if (payload.channelsDetails) {
      payload.channelsDetails = payload.channelsDetails.map((channel: any) => ({
        ...channel,
        logistics: undefined,
      }));
    }
  }
  if (mode === "multi_hub") {
    payload.logistics = { ...payload.logistics, hub: undefined };
    if (payload.channelsDetails) {
      payload.channelsDetails = payload.channelsDetails.map((channel: any) => ({
        ...channel,
        logistics: undefined,
      }));
    }
  }
  if (mode === "per_channel") {
    payload.logistics = { ...payload.logistics, hub: undefined, hubs: [] };
  }

  if (payload.channels) delete payload.channels;
  if (payload.countries) delete payload.countries;
  if (payload.entities) delete payload.entities;
  return payload;
};

const Distributeurs: React.FC = () => {
  const dispatch = useAppDispatch();
  const [selectedEmailsForProject, setSelectedEmailsForProject] = useState<string[]>([]);
  const [selectedProjectForAttach, setSelectedProjectForAttach] = useState<string | null>(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [activeProjectForTimeline, setActiveProjectForTimeline] = useState<string | null>(null);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isConversationDrawerOpen, setIsConversationDrawerOpen] = useState(false);
  const [isUnassignedDrawerOpen, setIsUnassignedDrawerOpen] = useState(false);
  const [selectedPeopleFilters, setSelectedPeopleFilters] = useState<string[]>([]);
  const [projectDragOverColumn, setProjectDragOverColumn] = useState<string | null>(null);
  const [activeMainTab, setactiveMainTab] = useState("network");
  const [crmTabs, setCrmTabs] = useState<CrmTabConfig[]>(() => {
    if (typeof window === "undefined") return DEFAULT_CRM_TABS;
    try {
      const stored = JSON.parse(window.localStorage.getItem(CRM_TABS_STORAGE_KEY) || "[]");
      if (Array.isArray(stored) && stored.length) {
        return stored
          .filter((tab) => tab?.key && tab?.label)
          .map(normalizeCrmTabConfig);
      }
    } catch {
      // Local storage is only a UI preference; bad data should not block the CRM.
    }
    return DEFAULT_CRM_TABS;
  });
  const [editingCrmTabKey, setEditingCrmTabKey] = useState<string | null>(null);
  const [editingCrmTabLabel, setEditingCrmTabLabel] = useState("");
  const [editingCrmTabIcon, setEditingCrmTabIcon] = useState("team");
  const [editingCrmValidatorUserId, setEditingCrmValidatorUserId] = useState<string | undefined>(undefined);
  const [editingCrmColumns, setEditingCrmColumns] = useState<CrmColumnConfig[]>([]);
  const [renameCrmTabOpen, setRenameCrmTabOpen] = useState(false);
  const [createCrmTabOpen, setCreateCrmTabOpen] = useState(false);
  const [createCrmTabName, setCreateCrmTabName] = useState("");
  const [activeDrawerTab, setActiveDrawerTab] = useState("overview");
  const [retailerEmails, setRetailerEmails] = useState<RetailerMailMessage[]>([]);
  const [retailerEmailsLoading, setRetailerEmailsLoading] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [projectForm] = Form.useForm();
  const [messageDraft, setMessageDraft] = useState("");

  const [aiLoadingByAction, setAiLoadingByAction] = useState<Record<string, boolean>>({});
  const [aiOutputsByAction, setAiOutputsByAction] = useState<Record<string, any>>({});
  const [aiWarningsByAction, setAiWarningsByAction] = useState<Record<string, string[]>>({});
  const [summaryLoadingById, setSummaryLoadingById] = useState<Record<string, boolean>>({});
  const autoSnapshotRef = useRef<{ projectId?: string; messageId?: string } | null>(null);
  const autoStrategyRef = useRef<{ projectId?: string; messageId?: string } | null>(null);

  const [loading, setLoading] = useState(false);
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [search, setSearch] = useState("");
  const [segment, setSegment] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<Retailer | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [createOnboardingForm] = Form.useForm();
  const createOnboardingScope = Form.useWatch(["kyc", "marketScopeType"], createOnboardingForm);
  const createOnboardingHasEshop = Form.useWatch(["kyc", "hasEshop"], createOnboardingForm);
  const createOnboardingValues = Form.useWatch([], createOnboardingForm);
  const [kycForm] = Form.useForm();
  const kycScope = Form.useWatch(["kyc", "marketScopeType"], kycForm);
  const kycHasEshop = Form.useWatch(["kyc", "hasEshop"], kycForm);
  const [creating, setCreating] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [savingEdit, setSavingEdit] = useState(false);
  const editFulfillmentMode = Form.useWatch(["logistics", "fulfillmentMode"], editForm);
  const createFulfillmentMode = Form.useWatch(["logistics", "fulfillmentMode"], createForm);
  const [createFlow, setCreateFlow] = useState<"onboarded" | "not_onboarded" | null>(null);
  const [accountManagers, setAccountManagers] = useState<User[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  
  const [mapChannel, setMapChannel] = useState("all");
  const [mapCountry, setMapCountry] = useState("all");
  const [mapRetailer, setMapRetailer] = useState("all");
  const [mailAlerts, setMailAlerts] = useState<Record<string, RetailerMailAlert>>({});
  const onboardingChecklistInitialized = useRef(new Set<string>());
  const projectChecklistInitialized = useRef(new Set<string>());
  const [draftContacts, setDraftContacts] = useState<any[]>([]);
  const [contactDrafts, setContactDrafts] = useState<Record<string, any>>({});
  const [contactEditing, setContactEditing] = useState<Record<string, boolean>>({});
  const [newContactDraft, setNewContactDraft] = useState({
    name: "",
    role: "",
    email: "",
    phone: "",
    isDecisionMaker: false,
    contactType: "general" as "general" | "accounting",
  });
  const [mapZoom, setMapZoom] = useState(2.1);
  const [mapCenter, setMapCenter] = useState<[number, number]>([2.3522, 48.8566]); // France center
  const mapRef = useRef<HTMLDivElement | null>(null);
  const [mapTooltip, setMapTooltip] = useState({
    content: "",
    x: 0,
    y: 0,
    visible: false,
  });
  const [crmStageFilter, setCrmStageFilter] = useState<string | undefined>(undefined);
  const [crmPriorityFilter, setCrmPriorityFilter] = useState<string | undefined>(undefined);
  const [crmBrandFilter, setCrmBrandFilter] = useState<string | undefined>(undefined);
  const [crmLeadSourceFilter, setCrmLeadSourceFilter] = useState<string | undefined>(undefined);
  const [crmProductFilter, setCrmProductFilter] = useState<string | undefined>(undefined);
  const [crmHasEshopFilter, setCrmHasEshopFilter] = useState<string | undefined>(undefined);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportColumns, setExportColumns] = useState(
    RETAILER_EXPORT_COLUMNS.map((column) => ({
      ...column,
      selected: column.defaultSelected,
      header: column.label,
    }))
  );
  const [exportCountry, setExportCountry] = useState("all");
  const [exportRetailersSelection, setExportRetailersSelection] = useState<string[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [kpiLoading, setKpiLoading] = useState(false);

  const { currentUser } = useAppSelector((state) => state.user);
  const activeCrmKey = activeMainTab.startsWith("crm:")
    ? activeMainTab.replace(/^crm:/, "")
    : DEFAULT_CRM_KEY;
  const activeCrmTab = crmTabs.find((tab) => tab.key === activeCrmKey) || DEFAULT_CRM_TABS[0];
  const activeCrmColumns = activeCrmTab.columns.length
    ? activeCrmTab.columns
    : DEFAULT_CRM_COLUMNS;
  const getCrmKey = (retailer: Retailer) => retailer.crm?.key || DEFAULT_CRM_KEY;
  const getCrmColumnKey = (retailer: Retailer) =>
    retailer.crm?.columnKey ||
    retailer.crm?.onboardingStage ||
    activeCrmColumns[0]?.key ||
    "lead";
  const getCreatedByLabel = (retailer: Retailer) => {
    const author = retailer.createdBy;
    const fullName = [author?.firstName, author?.lastName].filter(Boolean).join(" ").trim();
    return fullName || author?.email || "Non renseigné";
  };
  const getInitials = (value: string) => {
    const cleaned = value.trim();
    if (!cleaned || cleaned === "Non renseigné") return "?";
    const parts = cleaned
      .replace(/@.*/, "")
      .split(/\s+|[._-]+/)
      .filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  };
  const hotMemoryItems = useAppSelector((state) => {
    const uid = currentUser?.id || "";
    const byUser = (state as any).hotMemory?.itemsByUser || {};
    return uid && Array.isArray(byUser[uid]) ? byUser[uid] : [];
  });
  const hotMemoryUserId = currentUser?.id || "";
  const canAssignAccountManager =
    (currentUser?.accesses || []).includes("admin") ||
    (currentUser?.departmentFlags?.isAccountManagement &&
      (currentUser?.level === "manager1" || currentUser?.level === "manager2"));
  const selectableAccountManagers = useMemo(() => {
    return accountManagers.filter((user) => {
      const accesses = user.accesses || [];
      return Boolean(user.departmentFlags?.isAccountManagement || accesses.includes("admin"));
    });
  }, [accountManagers]);

  useEffect(() => {
    setCrmTabs((previous) => {
      const byKey = new Map<string, CrmTabConfig>();
      DEFAULT_CRM_TABS.forEach((tab) => byKey.set(tab.key, tab));
      previous.forEach((tab) => byKey.set(tab.key, tab));
      retailers.forEach((retailer) => {
        const key = getCrmKey(retailer);
        if (!byKey.has(key)) {
          byKey.set(key, normalizeCrmTabConfig({ key, label: key }));
        }
      });
      return Array.from(byKey.values());
    });
  }, [retailers]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CRM_TABS_STORAGE_KEY, JSON.stringify(crmTabs));
  }, [crmTabs]);

  useEffect(() => {
    setCrmStageFilter(undefined);
  }, [activeCrmKey]);

  const handleAddCrmTab = () => {
    setCreateCrmTabName("");
    setCreateCrmTabOpen(true);
  };

  const handleCreateCrmTab = () => {
    const label = createCrmTabName.trim() || "Nouveau CRM";
    const key = ensureUniqueCrmKey(label, crmTabs.map((tab) => tab.key));
    setCrmTabs((previous) => [
      ...previous,
      normalizeCrmTabConfig({ key, label, icon: "team" }),
    ]);
    setactiveMainTab(`crm:${key}`);
    setCreateCrmTabOpen(false);
  };

  const commitCrmTabRename = async () => {
    if (!editingCrmTabKey) return;
    const nextLabel = editingCrmTabLabel.trim();
    if (!nextLabel || editingCrmColumns.length === 0) {
      message.warning("Le CRM doit avoir un nom et au moins une colonne");
      return;
    }

    const currentTab = crmTabs.find((tab) => tab.key === editingCrmTabKey);
    const nextColumns = editingCrmColumns.map((column) => ({
      ...column,
      label: column.label.trim() || "Sans nom",
    }));
    const selectedValidator = accountManagers.find((user) => user._id === editingCrmValidatorUserId);
    const selectedValidatorName = selectedValidator
      ? [selectedValidator.firstName, selectedValidator.lastName].filter(Boolean).join(" ") ||
        selectedValidator.email ||
        selectedValidator._id
      : undefined;
    const nextColumnKeys = new Set(nextColumns.map((column) => column.key));
    const removedColumnKeys = (currentTab?.columns || [])
      .map((column) => column.key)
      .filter((key) => !nextColumnKeys.has(key));

    try {
      if (removedColumnKeys.length > 0) {
        await reassignRetailerCrm({
          fromCrmKey: editingCrmTabKey,
          fromColumnKeys: removedColumnKeys,
          toColumnKey: nextColumns[0].key,
        });
        setRetailers((previous) =>
          previous.map((retailer) =>
            getCrmKey(retailer) === editingCrmTabKey &&
            removedColumnKeys.includes(getCrmColumnKey(retailer))
              ? {
                  ...retailer,
                  crm: { ...(retailer.crm || {}), columnKey: nextColumns[0].key } as Retailer["crm"],
                }
              : retailer
          )
        );
      }

      setCrmTabs((previous) =>
        previous.map((tab) =>
          tab.key === editingCrmTabKey
            ? {
                ...tab,
                label: nextLabel,
                icon: editingCrmTabIcon,
                validatorUserId: editingCrmValidatorUserId,
                validatorUserName: selectedValidatorName,
                columns: nextColumns,
              }
            : tab
        )
      );
      setEditingCrmTabKey(null);
      setRenameCrmTabOpen(false);
      message.success("CRM mis à jour");
    } catch (error) {
      message.error("Impossible de mettre à jour le CRM");
    }
  };

  const beginCrmTabRename = (tab: CrmTabConfig) => {
    setEditingCrmTabKey(tab.key);
    setEditingCrmTabLabel(tab.label);
    setEditingCrmTabIcon(tab.icon || "team");
    setEditingCrmValidatorUserId(tab.validatorUserId);
    setEditingCrmColumns(tab.columns.map((column) => ({ ...column })));
    setRenameCrmTabOpen(true);
  };

  const handleAddCrmColumn = () => {
    const label = `Colonne ${editingCrmColumns.length + 1}`;
    const key = ensureUniqueCrmKey(label, editingCrmColumns.map((column) => column.key));
    setEditingCrmColumns((previous) => [
      ...previous,
      { key, label, color: "#94a3b8" },
    ]);
  };

  const handleDeleteCrm = () => {
    if (!editingCrmTabKey) return;
    const targetTab =
      crmTabs.find((tab) => tab.key === DEFAULT_CRM_KEY && tab.key !== editingCrmTabKey) ||
      crmTabs.find((tab) => tab.key !== editingCrmTabKey);

    if (!targetTab) {
      message.warning("Il faut conserver au moins un CRM");
      return;
    }

    Modal.confirm({
      title: `Supprimer ${editingCrmTabLabel} ?`,
      content: `Les leads seront déplacés vers ${targetTab.label}.`,
      okText: "Supprimer",
      okButtonProps: { danger: true },
      cancelText: "Annuler",
      onOk: async () => {
        const targetColumnKey = targetTab.columns[0]?.key || "lead";
        await reassignRetailerCrm({
          fromCrmKey: editingCrmTabKey,
          toCrmKey: targetTab.key,
          toColumnKey: targetColumnKey,
        });
        setRetailers((previous) =>
          previous.map((retailer) =>
            getCrmKey(retailer) === editingCrmTabKey
              ? {
                  ...retailer,
                  crm: {
                    ...(retailer.crm || {}),
                    key: targetTab.key,
                    columnKey: targetColumnKey,
                  } as Retailer["crm"],
                }
              : retailer
          )
        );
        setCrmTabs((previous) => previous.filter((tab) => tab.key !== editingCrmTabKey));
        setactiveMainTab(`crm:${targetTab.key}`);
        setRenameCrmTabOpen(false);
        setEditingCrmTabKey(null);
        message.success("CRM supprimé");
      },
    });
  };

  const kpiDateRange = useMemo(() => {
    return {
      start: dayjs().startOf("year").startOf("day").toISOString(),
      end: dayjs().endOf("day").toISOString(),
    };
  }, []);

  const fetchPurchaseOrders = useCallback(async () => {
    setKpiLoading(true);
    try {
      const data = await getAllPurchaseOrders({
        fromDate: kpiDateRange.start,
        toDate: kpiDateRange.end,
      });
      setPurchaseOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error("Erreur lors du chargement des KPIs retailers");
    } finally {
      setKpiLoading(false);
    }
  }, [kpiDateRange.end, kpiDateRange.start]);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [fetchPurchaseOrders]);

  const handleUpdateRetailer = async (values: any, explicitId?: string) => {
    const id = explicitId || selected?.id || selected?._id;
    if (!id) return;
    try {
      setSavingEdit(true);
      const payload = await enrichRetailerWithGeocoding(normalizePayload(values));
      await updateRetailer(id, payload);
      message.success("Distributeur mis à jour");
      setEditDrawerOpen(false);
      const data = await getRetailers({ search, segment });
      setRetailers(data);
      if (selected && (selected.id === id || selected._id === id)) {
        const updated = data.find(r => (r.id || r._id) === id);
        if (updated) setSelected(updated);
      }
    } catch (error) {
      message.error("Erreur lors de la mise à jour");
    } finally {
      setSavingEdit(false);
    }
  }

  const updateProjectInState = (retailerId: string, projectId: string, updates: Partial<Project>) => {
    setRetailers((prev) =>
      prev.map((retailer) => {
        const currentId = retailer.id || retailer._id;
        if (currentId !== retailerId) return retailer;
        const projects = (retailer.projects || []).map((project) =>
          project._id === projectId ? { ...project, ...updates } : project
        );
        return { ...retailer, projects };
      })
    );

    setSelected((prev) => {
      if (!prev) return prev;
      const currentId = prev.id || prev._id;
      if (currentId !== retailerId) return prev;
      const projects = (prev.projects || []).map((project) =>
        project._id === projectId ? { ...project, ...updates } : project
      );
      return { ...prev, projects };
    });
  };

  const DEFAULT_PROJECT_TASKS: Record<string, string[]> = {
    todo: ["Analyser la demande entrante", "Vérifier les stocks/faisabilité", "Préparer une réponse initiale"],
    in_progress: ["Envoyer la proposition au client", "Négocier les conditions", "Valider la commande finale"]
  };

  const getOnboardingProject = (projects?: Project[]) => {
    if (!projects || projects.length === 0) return undefined;
    return projects
      .slice()
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime())[0];
  };

  const getActiveProject = () => {
    if (!selected) return undefined;
    if (activeProjectForTimeline) {
      return (selected.projects || []).find((p) => p._id === activeProjectForTimeline);
    }
    return getOnboardingProject(selected.projects || []);
  };

  const buildProjectContext = (retailer: Retailer, project: Project): ProjectContextPayload => {
    const retailerId = retailer.id || retailer._id || "";
    const projectKey = project._id || `${retailerId}:${project.title}`;
    const projectType = retailer.crm?.onboardingStage && retailer.crm.onboardingStage !== "launched"
      ? "retailerOnboarding"
      : "other";
    const participants = Array.from(
      new Set(
        [
          retailer.accountManager?.email,
          ...(project.attachedEmailIds || []).flatMap((id) => {
            const msg = retailerEmails.find((email) => email.id === id);
            return msg?.from ? [msg.from] : [];
          }),
        ].filter(Boolean) as string[]
      )
    );
    const projectEmails = retailerEmails.filter((email) =>
      (email.id && (project.attachedEmailIds || []).includes(email.id)) ||
      (email.conversationId && (project.attachedConversationIds || []).includes(email.conversationId))
    );
    const latestEmail = projectEmails
      .slice()
      .sort((a, b) => new Date(b.receivedDateTime || "").getTime() - new Date(a.receivedDateTime || "").getTime())[0];
    const currentTasks = [
      ...(project.taskBoard?.todo || []),
      ...(project.taskBoard?.in_progress || []),
    ]
      .map((task: any) => task?.title)
      .filter((title: any) => typeof title === "string" && title.trim().length > 0);
    return {
      projectKey,
      projectType,
      title: project.title,
      description: project.description,
      lastEmailSubject: latestEmail?.subject || project.lastEmailSubject,
      lastEmailPreview: latestEmail?.preview || project.lastEmailPreview,
      lastEmailAt: latestEmail?.receivedDateTime || project.lastEmailAt,
      lastEmailFrom: latestEmail?.from || project.lastEmailFrom,
      participants,
      currentTasks,
      attachments: (project.attachments || []).map((att) => ({
        name: att.name,
        contentType: att.contentType,
      })),
      relatedEntities: retailerId ? { retailerId } : undefined,
      language: "FR",
    };
  };

  const buildHotMemoryPayload = () =>
    (hotMemoryItems || []).map((item: any) => ({
      id: item.id,
      type: item.type,
      label: item.label,
      data: item.data,
      pinned: item.pinned,
    }));

  const getLatestProjectMessage = (project: Project) => {
    const ids = project.attachedEmailIds || [];
    const conversations = project.attachedConversationIds || [];
    const msgs = retailerEmails.filter((email) =>
      (email.id && ids.includes(email.id)) ||
      (email.conversationId && conversations.includes(email.conversationId))
    );
    if (!msgs.length) return undefined;
    return msgs.sort(
      (a, b) => new Date(b.receivedDateTime || "").getTime() - new Date(a.receivedDateTime || "").getTime()
    )[0];
  };

  const forceProjectSnapshot = async (retailer: Retailer, project: Project) => {
    const latest = getLatestProjectMessage(project);
    await runAiProjectAction({
      action: "PROJECT_SNAPSHOT",
      retailer,
      project,
      target: latest?.id
        ? { threadId: latest.conversationId, messageId: latest.id }
        : undefined,
      auto: true,
    });
  };

  const runAiProjectAction = async ({
    action,
    retailer,
    project,
    target,
    auto = false,
  }: {
    action: ProjectActionType;
    retailer: Retailer;
    project: Project;
    target?: {
      threadId?: string;
      messageId?: string;
      attachmentId?: string;
      resourcePath?: string;
      docKey?: string;
      collectionName?: string;
    };
    auto?: boolean;
  }) => {
    if (!currentUser) return;
    const actionKey = `${action}:${project._id || project.title}`;
    setAiLoadingByAction((prev) => ({ ...prev, [actionKey]: true }));
    try {
      const result = await runProjectAction({
        action,
        projectContext: buildProjectContext(retailer, project),
        projectRef: {
          retailerId: retailer.id || retailer._id || "",
          projectId: project._id || "",
        },
        target,
        hotMemory: buildHotMemoryPayload(),
        styleProfile: {
          tone: "professionnel",
          closing: currentUser?.firstName ? `Cordialement, ${currentUser.firstName}` : "Cordialement",
          length: "medium",
          firmness: "neutre",
        },
        recipientProfile: {
          relation: project.pushPullStatus === "pull" ? "client" : "fournisseur",
          sensitivity: "standard",
          formality: "professionnel",
        },
      });

      setAiOutputsByAction((prev) => ({ ...prev, [actionKey]: result.structuredOutput }));
      setAiWarningsByAction((prev) => ({ ...prev, [actionKey]: result.warnings || [] }));

      if (action === "PROJECT_SNAPSHOT") {
        const metadata = {
          ...(project.metadata || {}),
          lastSnapshotMessageId: target?.messageId || "",
          lastSnapshotAt: new Date().toISOString(),
          snapshotPayload: JSON.stringify(result.structuredOutput || {}),
        } as Record<string, string>;
        const stageKey = project.pushPullStatus === "pull" ? "in_progress" : "todo";
        const snapshotTasks = extractSnapshotTasks(result?.structuredOutput || {});
        const existingBoard = project.taskBoard || {};
        const stageTasks = existingBoard?.[stageKey] || [];
        const existingTitles = new Set(stageTasks.map((task) => task.title));
        const mergedStageTasks = snapshotTasks.length
          ? [
              ...stageTasks,
              ...snapshotTasks
                .filter((task) => !existingTitles.has(task))
                .map((task) => ({
                  title: task,
                  status: "active",
                  source: "ai",
                  createdAt: new Date().toISOString(),
                })),
            ]
          : stageTasks;
        const taskBoard = {
          ...existingBoard,
          [stageKey]: mergedStageTasks,
        };

        await updateProject(retailer.id || retailer._id || "", project._id || "", { metadata, taskBoard });
        updateProjectInState(retailer.id || retailer._id || "", project._id || "", { metadata, taskBoard });
        if (!auto) {
          message.success("Snapshot IA généré");
        }
      }

      if (action === "STRATEGIC_ADVICE") {
        const metadata = {
          ...(project.metadata || {}),
          lastStrategyMessageId: target?.messageId || "",
          lastStrategyAt: new Date().toISOString(),
          strategyPayload: JSON.stringify(result.structuredOutput || {}),
        } as Record<string, string>;
        await updateProject(retailer.id || retailer._id || "", project._id || "", { metadata });
        updateProjectInState(retailer.id || retailer._id || "", project._id || "", { metadata });
      }

      return result;
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Action IA impossible: ${msg}`);
      return undefined;
    } finally {
      setAiLoadingByAction((prev) => ({ ...prev, [actionKey]: false }));
    }
  };

  const getSnapshotPayload = (project: Project) => {
    const snapshotKey = `PROJECT_SNAPSHOT:${project._id || project.title}`;
    const live = aiOutputsByAction[snapshotKey];
    if (live) return live;
    const raw = project.metadata?.snapshotPayload;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const extractSnapshotTasks = (payload: any): string[] => {
    const tasks = Array.isArray(payload?.tasks) ? payload.tasks : [];
    return tasks
      .map((task: any) => task?.title || task?.task || task?.label)
      .filter((task: any) => typeof task === "string" && task.trim().length > 0);
  };

  const parseDraftReply = (output?: Record<string, any>) => {
    if (!output) return "";
    return (
      output?.draftReply?.body ||
      output?.draftReply ||
      output?.reply ||
      output?.email?.body ||
      output?.body ||
      ""
    );
  };

  const summarizeMessage = async (retailerId: string, mail: RetailerMailMessage) => {
    if (!mail?.id) return;
    const key = mail.id;
    setSummaryLoadingById((prev) => ({ ...prev, [key]: true }));
    try {
      const result = await summarizeRetailerEmail(retailerId, mail.id, {
        subject: mail.subject,
        preview: mail.preview,
      });
      setRetailerEmails((prev) =>
        prev.map((item) =>
          item.id === mail.id ? { ...item, summary: result.summary } : item
        )
      );
    } catch (error) {
      message.error("Erreur lors du résumé IA");
    } finally {
      setSummaryLoadingById((prev) => ({ ...prev, [key]: false }));
    }
  };

  useEffect(() => {
    const project = getActiveProject();
    if (!selected || !project) return;
    const latest = getLatestProjectMessage(project);
    if (!latest?.id) return;
    const lastSnapshot = (project as any)?.metadata?.lastSnapshotMessageId;
    if (lastSnapshot === latest.id) return;

    if (
      autoSnapshotRef.current?.projectId === project._id &&
      autoSnapshotRef.current?.messageId === latest.id
    ) {
      return;
    }

    autoSnapshotRef.current = { projectId: project._id, messageId: latest.id };
    void runAiProjectAction({
      action: "PROJECT_SNAPSHOT",
      retailer: selected,
      project,
      target: {
        threadId: latest.conversationId,
        messageId: latest.id,
      },
      auto: true,
    });

    if (
      autoStrategyRef.current?.projectId === project._id &&
      autoStrategyRef.current?.messageId === latest.id
    ) {
      return;
    }

    autoStrategyRef.current = { projectId: project._id, messageId: latest.id };
    void runAiProjectAction({
      action: "STRATEGIC_ADVICE",
      retailer: selected,
      project,
      target: {
        threadId: latest.conversationId,
        messageId: latest.id,
      },
      auto: true,
    });
  }, [selected, retailerEmails, activeProjectForTimeline]);

  const handleProjectDrop = async ({ retailerId, project, target }: { retailerId: string; project: Project; target: string }) => {
    if (!retailerId || !project?._id) return;
    const previous = { status: project.status, pushPullStatus: project.pushPullStatus, taskBoard: project.taskBoard };
    
    let updates: Partial<Project> = {};
    const currentTaskBoard = project.taskBoard || {};
    const updatedTaskBoard = { ...currentTaskBoard };

    if (target === 'todo') {
      updates = { status: 'open', pushPullStatus: 'push' };
      if (!updatedTaskBoard.todo || updatedTaskBoard.todo.length === 0) {
        updatedTaskBoard.todo = DEFAULT_PROJECT_TASKS.todo.map(task => ({
          title: task,
          status: 'active',
          source: 'default',
          createdAt: new Date().toISOString(),
        }));
      }
    } else if (target === 'in_progress') {
      updates = { status: 'open', pushPullStatus: 'pull' };
      if (!updatedTaskBoard.in_progress || updatedTaskBoard.in_progress.length === 0) {
        updatedTaskBoard.in_progress = DEFAULT_PROJECT_TASKS.in_progress.map(task => ({
          title: task,
          status: 'active',
          source: 'default',
          createdAt: new Date().toISOString(),
        }));
      }
    } else {
      updates = { status: 'completed' };
    }

    updates.taskBoard = updatedTaskBoard;

    updateProjectInState(retailerId, project._id || '', updates);

    try {
      await updateProject(retailerId, project._id || '', updates);
    } catch (error) {
      updateProjectInState(retailerId, project._id || '', previous);
      message.error("Impossible de déplacer le sujet. Retour à l'état précédent.");
    }
  };

  const handleToggleProjectTodo = async (retailerId: string, projectId: string, stage: 'todo' | 'in_progress', todoIndex: number) => {
    const project = retailers.find(r => (r.id || r._id) === retailerId)?.projects?.find(p => p._id === projectId);
    if (!project) return;

    const currentTaskBoard = project.taskBoard || {};
    const stageTasks = [...(currentTaskBoard[stage] || [])];
    if (!stageTasks[todoIndex]) return;

    const current = stageTasks[todoIndex];
    const nextStatus = current.status === 'archived' ? 'active' : 'archived';
    stageTasks[todoIndex] = {
      ...current,
      status: nextStatus,
      archivedAt: nextStatus === 'archived' ? new Date().toISOString() : undefined,
    };

    const updates = {
      taskBoard: {
        ...currentTaskBoard,
        [stage]: stageTasks,
      }
    };

    updateProjectInState(retailerId, projectId, updates);

    try {
      await updateProject(retailerId, projectId, updates);
      const selectedRetailer = retailers.find(r => (r.id || r._id) === retailerId);
      if (selectedRetailer) {
        await forceProjectSnapshot(selectedRetailer, { ...project, ...updates });
      }
    } catch (error) {
      message.error("Erreur lors de la mise à jour de la tâche");
      updateProjectInState(retailerId, projectId, { taskBoard: currentTaskBoard });
    }
  };

  const handleAddProjectTodo = async (retailerId: string, projectId: string, stage: 'todo' | 'in_progress', task: string) => {
    if (!task.trim()) return;
    const project = retailers.find(r => (r.id || r._id) === retailerId)?.projects?.find(p => p._id === projectId);
    if (!project) return;

    const currentTaskBoard = project.taskBoard || {};
    const stageTasks = [...(currentTaskBoard[stage] || [])];
    stageTasks.push({
      title: task,
      status: 'active',
      source: 'manual',
      createdAt: new Date().toISOString(),
    });

    const updates = {
      taskBoard: {
        ...currentTaskBoard,
        [stage]: stageTasks,
      }
    };

    updateProjectInState(retailerId, projectId, updates);

    try {
      await updateProject(retailerId, projectId, updates);
      const selectedRetailer = retailers.find(r => (r.id || r._id) === retailerId);
      if (selectedRetailer) {
        await forceProjectSnapshot(selectedRetailer, { ...project, ...updates });
      }
    } catch (error) {
      message.error("Erreur lors de l'ajout de la tâche");
      updateProjectInState(retailerId, projectId, { taskBoard: currentTaskBoard });
    }
  };

  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [crmDragOverColumn, setCrmDragOverColumn] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, retailerId: string) => {
    setDraggedId(retailerId);
    e.dataTransfer.setData("retailerId", retailerId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setCrmDragOverColumn(null);
  };

  const DEFAULT_STAGE_TASKS: Record<string, string[]> = {
    lead: ["Identifier les décideurs", "Premier contact sortant", "Qualifier le potentiel initial", "Faire valider aux marques"],
    discovery: ["Réunion de découverte", "Comprendre les besoins logistiques", "Identifier les canaux cibles"],
    qualified: ["Valider le Business Case", "Vérification KYC initiale", "Présenter les conditions commerciales"],
    negotiation: ["Discussions commerciales", "Aligner les conditions logistiques", "Valider l'intérêt mutuel"],
    contract: ["Signatures du contrat", "Collecter les documents administratifs", "Paramétrage initial"],
    onboarding: ["Kick-off opérationnel", "Synchroniser les flux logistiques", "Installer les outils internes"],
    launched: ["Activation finale", "Monitoring premiers flux", "Validation des KPI"]
  };

  const createOnboardingDraftRetailer = useMemo(() => {
    const values = createOnboardingValues || {};
    const leadDefaults = DEFAULT_STAGE_TASKS.lead.map((task) => ({ task, completed: false }));
    const leadChecklist = values.crm?.checklists?.lead?.length ? values.crm.checklists.lead : leadDefaults;
    return {
      name: values.name || "",
      group: values.group || "",
      segment: values.segment || "C",
      crm: {
        onboardingStage: "lead",
        onboardingPriority: values.crm?.onboardingPriority || "medium",
        proposedBrand: values.crm?.proposedBrand || undefined,
        checklists: {
          lead: leadChecklist,
        },
      },
      kyc: {
        marketScopeType: values.kyc?.marketScopeType || "cities",
        ...(values.kyc || {}),
      },
      ...(canAssignAccountManager ? { accountManager: values.accountManager || undefined } : {}),
      contacts: values.contacts || draftContacts,
    } as Retailer;
  }, [createOnboardingValues, draftContacts]);

  const handleDrop = async (e: React.DragEvent, targetColumn: CrmColumnConfig) => {
    e.preventDefault();
    setCrmDragOverColumn(null);
    const retailerId = e.dataTransfer.getData("retailerId");
    if (!retailerId) return;

    const retailerIndex = retailers.findIndex((r) => (r.id || r._id) === retailerId);
    if (retailerIndex === -1) return;

    const retailer = retailers[retailerIndex];
    if (getCrmColumnKey(retailer) === targetColumn.key) {
      setDraggedId(null);
      return;
    }
    if (activeCrmTab.validatorUserId && !retailer.crm?.validation?.approvedAt) {
      message.warning(
        `Prospect en attente de validation par ${activeCrmTab.validatorUserName || "le validateur CRM"}`
      );
      setDraggedId(null);
      return;
    }

    const originalRetailers = [...retailers];
    const now = new Date().toISOString();
    const targetStage = targetColumn.stage;
    const currentHistory = retailer.crm?.stageHistory || {};
    const currentChecklists = retailer.crm?.checklists || {};
    const updatedChecklists = { ...currentChecklists };

    if (targetStage && !currentChecklists[targetStage]?.length) {
      const defaultTasks = DEFAULT_STAGE_TASKS[targetStage] || [];
      updatedChecklists[targetStage] = defaultTasks.map(task => ({
        task,
        completed: false
      })) as any;
    }

    const newStatus = targetStage === "launched" ? "active" : (retailer.status === "active" ? "active" : "prospect");

    const updatedRetailerOptimistic = {
      ...retailer,
      status: newStatus as any,
      crm: {
        ...(retailer.crm || {}),
        columnKey: targetColumn.key,
        ...(targetStage ? { onboardingStage: targetStage } : {}),
        checklists: updatedChecklists,
        stageHistory: targetStage
          ? {
              ...currentHistory,
              [targetStage]: { reached: true, reachedAt: now }
            }
          : currentHistory,
      }
    };

    const newRetailers = [...retailers];
    newRetailers[retailerIndex] = updatedRetailerOptimistic;
    setRetailers(newRetailers);
    setDraggedId(null);

    try {
      const formValues = toFormValues(updatedRetailerOptimistic);
      await handleUpdateRetailer(formValues, retailer.id || retailer._id);
      message.success(`Déplacé vers ${targetColumn.label}`);
    } catch (error) {
      console.error("Drop error:", error);
      setRetailers(originalRetailers); // ROLLBACK
      message.error("Erreur lors du déplacement, rétablissement...");
    }
  };

  const renderCRMSection = () => {
    return (
      <div className="crm-pipeline">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <Select
            allowClear
            placeholder="Étape"
            value={crmStageFilter}
            onChange={setCrmStageFilter}
            style={{ minWidth: 160 }}
            options={activeCrmColumns.map((column) => ({
              value: column.key,
              label: column.label,
            }))}
          />
          <Select
            allowClear
            placeholder="Priorité"
            value={crmPriorityFilter}
            onChange={setCrmPriorityFilter}
            style={{ minWidth: 150 }}
            options={[
              { value: "high", label: "Niveau 1" },
              { value: "medium", label: "Niveau 2" },
              { value: "low", label: "Niveau 3" },
            ]}
          />
          <Select
            allowClear
            placeholder="Produit phare"
            value={crmProductFilter}
            onChange={setCrmProductFilter}
            style={{ minWidth: 180 }}
            options={crmProductOptions}
            showSearch
          />
          <Select
            allowClear
            placeholder="Marque proposée"
            value={crmBrandFilter}
            onChange={setCrmBrandFilter}
            style={{ minWidth: 180 }}
            options={crmBrandOptions}
            showSearch
          />
          <Select
            allowClear
            placeholder="Lead source"
            value={crmLeadSourceFilter}
            onChange={setCrmLeadSourceFilter}
            style={{ minWidth: 170 }}
            options={crmLeadSourceOptions}
            showSearch
          />
          <Select
            allowClear
            placeholder="E-shop"
            value={crmHasEshopFilter}
            onChange={setCrmHasEshopFilter}
            style={{ minWidth: 120 }}
            options={[
              { value: "yes", label: "Oui" },
              { value: "no", label: "Non" },
            ]}
          />
        </div>
        <div
          style={{
            overflowX: "hidden",
            paddingBottom: 16,
            width: "100%",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${activeCrmColumns.length}, minmax(0, 1fr))`,
              gap: activeCrmColumns.length >= 8 ? 6 : activeCrmColumns.length >= 6 ? 8 : 12,
              width: "100%",
              minWidth: 0,
            }}
          >
            {activeCrmColumns.map((column) => {
            const groupRetailers = filteredRetailers.filter(
              (retailer) => getCrmColumnKey(retailer) === column.key
            );
            const totalValue = groupRetailers.reduce(
              (sum, r) => sum + (r.crm?.estimatedValue || 0),
              0
            );
            const isOver = crmDragOverColumn === column.key;

            return (
              <div key={column.key} style={{ minWidth: 0, display: "flex" }}>
                <div
                  className={`pipeline-column ${isOver ? "drag-over" : ""}`}
                  style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, overflow: "hidden" }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (crmDragOverColumn !== column.key) {
                      setCrmDragOverColumn(column.key);
                    }
                  }}
                  onDragLeave={() => setCrmDragOverColumn(null)}
                  onDrop={(e) => handleDrop(e, column)}
                >
                  <div className="column-header" style={{ borderTop: `4px solid ${column.color}`, padding: '12px 8px' }}>
                    <div className="header-top">
                      <Space size={4}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: column.color }} />
                        <Typography.Text
                          strong
                          ellipsis={{ tooltip: column.label }}
                          style={{ fontSize: 12, minWidth: 0 }}
                        >
                          {column.label}
                        </Typography.Text>
                      </Space>
                      <Tag className="count-tag" style={{ margin: 0, padding: '0 4px', fontSize: 10 }}>{groupRetailers.length}</Tag>
                    </div>
                    <div className="header-bottom">
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {formatCurrency(totalValue)}
                      </Typography.Text>
                    </div>
                  </div>
                  <div className="column-content" style={{ minHeight: "550px", padding: '0', flex: 1 }}>
                    {groupRetailers.length === 0 ? (
                      <div className="empty-stage">Déposer un prospect ici</div>
                    ) : (
                      groupRetailers.map((retailer) => {
                        const rId = (retailer.id || retailer._id)!;
                        const isDragging = draggedId === rId;
                        const countries = getRetailerCountries(retailer);
                        const onboardingPriority = retailer.crm?.onboardingPriority || "medium";
                        const onboardingPriorityColor =
                          onboardingPriority === "high" ? "#ef4444" : onboardingPriority === "low" ? "#38bdf8" : "#f97316";
                        const segmentColor = retailer.segment === "A" ? "#0f172a" : retailer.segment === "B" ? "#0038D1" : "#8AA9FF";
                        const alertCount = mailAlerts[rId]?.count || 0;
                        const createdByLabel = getCreatedByLabel(retailer);
                        const validation = retailer.crm?.validation;
                        const canValidateCrm =
                          Boolean(currentUser?.accesses?.includes("admin")) ||
                          String(currentUser?.id || currentUser?._id || "") === String(activeCrmTab.validatorUserId || "");
                        return (
                          <div
                            key={rId}
                            draggable
                            onDragStart={(e) => handleDragStart(e, rId)}
                            onDragEnd={handleDragEnd}
                            style={{ opacity: isDragging ? 0.4 : 1, transition: 'opacity 0.2s' }}
                          >
                            <Card
                              className={`crm-card ${isDragging ? "is-dragging" : ""}`}
                              hoverable
                              size="small"
                              styles={{ body: { padding: '12px' } }}
                              onClick={() => {
                                setSelected(retailer);
                                setDrawerOpen(true);
                              }}
                            >
                              <div className="card-top" style={{ marginBottom: 8 }}>
                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                                  <Space size={6} wrap>
                                    <Typography.Text strong style={{ fontSize: 13, color: segmentColor }}>{retailer.name}</Typography.Text>
                                    {alertCount > 0 && <Badge count={alertCount} />}
                                  </Space>
                                  <Tooltip title={`Ajouté par ${createdByLabel}`}>
                                    <Avatar size={24} style={{ flex: "0 0 auto", background: "#0f172a", color: "#ffffff", fontSize: 10, fontWeight: 700 }}>
                                      {getInitials(createdByLabel)}
                                    </Avatar>
                                  </Tooltip>
                                </div>
                              </div>
                              {activeCrmTab.validatorUserId && (
                                <div style={{ marginBottom: 8 }}>
                                  {validation?.approvedAt ? (
                                    <Tag color="green" style={{ margin: 0 }}>Validé</Tag>
                                  ) : (
                                    <Space size={6} wrap>
                                      <Tag color="gold" style={{ margin: 0 }}>
                                        Validation requise
                                      </Tag>
                                      {canValidateCrm && (
                                        <Button
                                          size="small"
                                          type="link"
                                          style={{ padding: 0, height: 18, fontSize: 11 }}
                                          onClick={async (event) => {
                                            event.stopPropagation();
                                            const approvedAt = new Date().toISOString();
                                            const approverName = [
                                              currentUser?.firstName,
                                              currentUser?.lastName,
                                            ].filter(Boolean).join(" ") || currentUser?.email || "Admin";
                                            const nextRetailer = {
                                              ...retailer,
                                              crm: {
                                                ...(retailer.crm || {}),
                                                validation: {
                                                  approvedAt,
                                                  approvedBy: String(currentUser?.id || currentUser?._id || ""),
                                                  approvedByName: approverName,
                                                },
                                              },
                                            };
                                            setRetailers((previous) =>
                                              previous.map((item) =>
                                                (item.id || item._id) === rId ? nextRetailer : item
                                              )
                                            );
                                            try {
                                              await updateRetailer(rId, normalizePayload(toFormValues(nextRetailer)));
                                              message.success("Prospect validé");
                                            } catch {
                                              message.error("Impossible de valider le prospect");
                                              setRetailers((previous) =>
                                                previous.map((item) =>
                                                  (item.id || item._id) === rId ? retailer : item
                                                )
                                              );
                                            }
                                          }}
                                        >
                                          Valider
                                        </Button>
                                      )}
                                    </Space>
                                  )}
                                </div>
                              )}
                              
                              <div className="card-middle">
                                <Space size={[4, 4]} wrap style={{ marginBottom: 8 }}>
                                  {(countries.length ? countries : ["Europe"]).map((location) => (
                                    <Tag
                                      key={location}
                                      color="blue"
                                      style={{ margin: 0, fontSize: 10, borderRadius: 10, padding: '0 6px', maxWidth: '100%', whiteSpace: 'normal' }}
                                    >
                                      {location}
                                    </Tag>
                                  ))}
                                </Space>
                                
                                <div className="progress-container" style={{ background: '#f1f5f9', borderRadius: 4, padding: '6px 8px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <Typography.Text style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>PROGRESSION</Typography.Text>
                                    <Typography.Text style={{ fontSize: 10, fontWeight: 700, color: column.color }}>
                                      {(() => {
                                        const stage = retailer.crm?.onboardingStage || "lead";
                                        const checklist = (retailer.crm?.checklists as any)?.[stage] || [];
                                        if (checklist.length === 0) return "0%";
                                        const completed = checklist.filter((t: any) => t.completed).length;
                                        return `${Math.round((completed / checklist.length) * 100)}%`;
                                      })()}
                                    </Typography.Text>
                                  </div>
                                  <Progress
                                    percent={(() => {
                                      const stage = retailer.crm?.onboardingStage || "lead";
                                      const checklist = (retailer.crm?.checklists as any)?.[stage] || [];
                                      if (checklist.length === 0) return 0;
                                      const completed = checklist.filter((t: any) => t.completed).length;
                                      return Math.round((completed / checklist.length) * 100);
                                    })()}
                                    size="small"
                                    showInfo={false}
                                    strokeColor={column.color}
                                    style={{ margin: 0 }}
                                  />
                                </div>
                              </div>
                              
                              <div className="card-footer" style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography.Text type="secondary" style={{ fontSize: 9, textTransform: 'uppercase' }}>VALEUR EST.</Typography.Text>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: onboardingPriorityColor, display: 'inline-block' }} />
                                    <Typography.Text strong style={{ fontSize: 12, color: '#0f172a' }}>
                                      {formatCurrency(retailer.crm?.estimatedValue || 0)}
                                    </Typography.Text>
                                  </div>
                                </div>
                                
                                {retailer.crm?.nextActionDate && (
                                  <Tooltip title={retailer.crm.nextActionLabel}>
                                    <div style={{ textAlign: 'right' }}>
                                      <Typography.Text type="secondary" style={{ fontSize: 9, textTransform: 'uppercase', display: 'block' }}>RAPPEL</Typography.Text>
                                      <Tag
                                        icon={<ClockCircleOutlined style={{ fontSize: 9 }} />}
                                        color={dayjs(retailer.crm.nextActionDate).isBefore(dayjs()) ? "red" : "default"}
                                        style={{ margin: 0, fontSize: 10, borderRadius: 4 }}
                                      >
                                        {dayjs(retailer.crm.nextActionDate).format("DD/MM")}
                                      </Tag>
                                    </div>
                                  </Tooltip>
                                )}
                              </div>
                            </Card>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      </div>
    );
  };

  const getTooltipPoint = (event: React.MouseEvent) => {
    if (!mapRef.current) return { x: 0, y: 0 };
    const rect = mapRef.current.getBoundingClientRect();
    const nativeEvent = event.nativeEvent as MouseEvent;
    const offsetX = (nativeEvent as any).offsetX;
    const offsetY = (nativeEvent as any).offsetY;
    if (typeof offsetX === "number" && typeof offsetY === "number") {
      return { x: offsetX, y: offsetY };
    }
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const updateTooltipPosition = (event: React.MouseEvent) => {
    setMapTooltip((prev) => {
      if (!prev.visible) return prev;
      const { x, y } = getTooltipPoint(event);
      return { ...prev, x, y };
    });
  };

  const showTooltip = (content: string, event: React.MouseEvent) => {
    const { x, y } = getTooltipPoint(event);
    setMapTooltip({ content, x, y, visible: true });
  };

  const handleMapMoveEnd = (position: { k: number; coordinates: [number, number] }) => {
    if (position.k && position.coordinates) {
      setMapZoom(position.k);
      setMapCenter(position.coordinates);
    }
  };

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const data = await getAllSuppliers();
        setSuppliers(data || []);
      } catch (error) {
        message.error("Erreur lors du chargement des fournisseurs");
      }
    };
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (!canAssignAccountManager) return;
    const fetchUsers = async () => {
      try {
        const users = await getActiveUsers();
        setAccountManagers(Array.isArray(users) ? users : []);
      } catch (error) {
        message.error("Erreur lors du chargement des utilisateurs");
      }
    };
    fetchUsers();
  }, [canAssignAccountManager]);

  const supplierOptions = useMemo(() => {
    return (suppliers || [])
      .filter((supplier) => supplier.name)
      .map((supplier) => ({
        value: supplier.name,
        label: supplier.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [suppliers]);

  useEffect(() => {
    if (drawerOpen && selected) {
      kycForm.resetFields();
      const fetchEmails = async () => {
        setRetailerEmailsLoading(true);
        try {
          const emails = await getRetailerEmails(selected.id || selected._id || "");
          setRetailerEmails(emails);
        } catch (error) {
          console.error("Erreur lors du chargement des emails:", error);
        } finally {
          setRetailerEmailsLoading(false);
        }
      };
      fetchEmails();
      if (selected.crm?.onboardingStage !== "launched") {
        setActiveDrawerTab("cockpit");
      }
      kycForm.setFieldsValue({
        name: selected.name,
        group: selected.group,
        segment: selected.segment,
        crm: {
          estimatedValue: selected.crm?.estimatedValue,
          proposedBrand: selected.crm?.proposedBrand,
        },
        kyc: {
          marketScopeType: selected.kyc?.marketScopeType || "cities",
          ...(selected.kyc || {}),
        },
      });
    } else {
      setRetailerEmails([]);
      setRetailerEmailsLoading(false);
      setActiveDrawerTab("overview");
    }
  }, [drawerOpen, selected, kycForm]);

  useEffect(() => {
    if (!selected || selected.crm?.onboardingStage === "launched") return;
    const retailerId = selected.id || selected._id;
    if (!retailerId || retailerEmails.length === 0) return;
    const emailIds = retailerEmails.map((email) => email.id).filter(Boolean) as string[];
    if (!emailIds.length) return;

    const syncOnboardingEmails = async () => {
      const onboardingProject = getOnboardingProject(selected.projects || []);
      if (!onboardingProject?._id) {
        try {
          await createProject(retailerId, {
            title: "Onboarding Retailer",
            status: "open",
            priority: "medium",
            pushPullStatus: "push",
            attachedEmailIds: emailIds,
          });
          const data = await getRetailers({ search, segment });
          setRetailers(data);
          const updated = data.find((retailer) => (retailer.id || retailer._id) === retailerId);
          if (updated) setSelected(updated);
        } catch (error) {
          message.error("Erreur lors de la création du projet onboarding");
        }
        return;
      }

      const currentAttached = onboardingProject.attachedEmailIds || [];
      const missing = emailIds.filter((id) => !currentAttached.includes(id));
      if (missing.length === 0) return;

      const merged = Array.from(new Set([...currentAttached, ...emailIds]));
      updateProjectInState(retailerId, onboardingProject._id, { attachedEmailIds: merged });
      try {
        await updateProject(retailerId, onboardingProject._id, { attachedEmailIds: merged });
      } catch (error) {
        updateProjectInState(retailerId, onboardingProject._id, { attachedEmailIds: currentAttached });
        message.error("Erreur lors de la mise à jour du projet onboarding");
      }
    };

    void syncOnboardingEmails();
  }, [selected, retailerEmails, search, segment]);

  useEffect(() => {
    if (!selected) return;
    const projects = selected.projects || [];
    const activeProject = activeProjectForTimeline
      ? projects.find((project) => project._id === activeProjectForTimeline)
      : projects.slice().sort((a, b) => new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime())[0];
    if (!activeProject?._id) return;
    const stageKey = activeProject.pushPullStatus === 'pull' ? 'in_progress' : 'todo';
    const stageTasks = activeProject.taskBoard?.[stageKey] || [];
    if (stageTasks.length > 0) return;
    const initKey = `${activeProject._id}-${stageKey}`;
    if (projectChecklistInitialized.current.has(initKey)) return;
    projectChecklistInitialized.current.add(initKey);
    const tasks = DEFAULT_PROJECT_TASKS[stageKey]?.map(task => ({
      title: task,
      status: 'active',
      source: 'default',
      createdAt: new Date().toISOString(),
    })) || [];
    const nextTaskBoard = { ...(activeProject.taskBoard || {}), [stageKey]: tasks };
    updateProjectInState(selected.id || selected._id || "", activeProject._id, { taskBoard: nextTaskBoard });
    void updateProject(selected.id || selected._id || "", activeProject._id, { taskBoard: nextTaskBoard });
  }, [selected, activeProjectForTimeline]);

  const handleSaveProject = async (values: any) => {
    if (!selected) return;
    try {
      if (editingProject) {
        await updateProject(selected.id || selected._id || "", editingProject._id!, {
          ...values,
          attachedEmailIds: editingProject.attachedEmailIds || [],
        });
        message.success("Projet mis à jour");
      } else {
        await createProject(selected.id || selected._id || "", {
          ...values,
          attachedEmailIds: selectedEmailsForProject,
        });
        message.success("Projet créé avec les emails sélectionnés");
      }
      setIsProjectModalOpen(false);
      projectForm.resetFields();
      setEditingProject(null);
      setSelectedEmailsForProject([]);
      const data = await getRetailers({ search, segment });
      setRetailers(data);
      const updated = data.find(r => (r.id || r._id) === (selected.id || selected._id));
      if (updated) setSelected(updated);
    } catch (error) {
      message.error("Erreur lors de l'enregistrement du projet");
    }
  };

  const applyChecklistDefaults = async (retailer: Retailer, stage: string) => {
    const defaultTasks = DEFAULT_STAGE_TASKS[stage] || [];
    const tasks = defaultTasks.map(task => ({ task, completed: false }));
    const updatedRetailer: Retailer = {
      ...retailer,
      crm: {
        onboardingStage: (retailer.crm?.onboardingStage || "lead") as any,
        ...(retailer.crm || {}),
        checklists: {
          ...(retailer.crm?.checklists || {}),
          [stage]: tasks,
        },
      },
    };
    const formValues = toFormValues(updatedRetailer);
    await handleUpdateRetailer(formValues, retailer.id || retailer._id);
    setSelected(updatedRetailer);
  };

  useEffect(() => {
    if (!drawerOpen || !selected) return;
    const stage = selected.crm?.onboardingStage || "lead";
    const checklist = (selected.crm?.checklists as any)?.[stage] || [];
    const checklistKey = `${selected.id || selected._id || selected.name}-${stage}`;
    if (checklist.length > 0 || onboardingChecklistInitialized.current.has(checklistKey)) return;
    onboardingChecklistInitialized.current.add(checklistKey);
    void applyChecklistDefaults(selected, stage);
  }, [drawerOpen, selected]);

  const handleDeleteProject = async (projectId: string) => {
    if (!selected) return;
    try {
      await deleteProject(selected.id || selected._id || "", projectId);
      message.success("Projet supprimé");
      const data = await getRetailers({ search, segment });
      setRetailers(data);
      const updated = data.find(r => (r.id || r._id) === (selected.id || selected._id));
      if (updated) setSelected(updated);
    } catch (error) {
      message.error("Erreur lors de la suppression");
    }
  };

  const updateDraftCrmChecklist = (stage: string, nextChecklist: any[]) => {
    const currentChecklists = createOnboardingForm.getFieldValue(["crm", "checklists"]) || {};
    createOnboardingForm.setFieldsValue({
      crm: {
        ...(createOnboardingForm.getFieldValue("crm") || {}),
        checklists: {
          ...currentChecklists,
          [stage]: nextChecklist,
        },
      },
    });
  };

  const threadDrawer = (
    <ThreadDrawer
      open={isConversationDrawerOpen}
      activeConversationId={activeConversationId}
      emails={retailerEmails}
      onClose={() => {
        setIsConversationDrawerOpen(false);
        setActiveConversationId(null);
      }}
      onSummarizeMessage={(msg) => {
        const retailerId = selected?.id || selected?._id || "";
        if (!retailerId) return;
        summarizeMessage(retailerId, msg);
      }}
      summaryLoadingById={summaryLoadingById}
      onGenerateDraftReply={async ({ latestMessage }) => {
        const activeProject = getActiveProject();
        const retailerId = selected?.id || selected?._id || "";
        if (!activeProject || !retailerId) return undefined;
        const result = await runAiProjectAction({
          action: "DRAFT_REPLY",
          retailer: selected as Retailer,
          project: activeProject,
          target: {
            threadId: latestMessage?.conversationId,
            messageId: latestMessage?.id,
          },
        });
        const draft = parseDraftReply(result?.structuredOutput);
        return draft || undefined;
      }}
      isGeneratingDraft={Boolean(
        getActiveProject() && aiLoadingByAction[`DRAFT_REPLY:${getActiveProject()?._id || getActiveProject()?.title}`]
      )}
      messageDraft={messageDraft}
      onMessageDraftChange={setMessageDraft}
      onUpdateMessage={(messageId, updates) =>
        setRetailerEmails((prev) => prev.map((item) => (item.id === messageId ? { ...item, ...updates } : item)))
      }
      onToggleImportant={async (messageId, nextImportant) => {
        const retailerId = selected?.id || selected?._id || "";
        if (!retailerId) return;
        await updateRetailerEmailImportance(retailerId, messageId, nextImportant);
      }}
      onSummarizeThread={selected
        ? async (threadId) => {
            const retailerId = selected.id || selected._id || "";
            if (!retailerId) return undefined;
            const result = await summarizeRetailerThread(retailerId, threadId);
            setRetailerEmails((prev) =>
              prev.map((item) =>
                item.conversationId === threadId ? { ...item, threadSummary: result.summary } : item
              )
            );
            return result.summary;
          }
        : undefined}
    />
  );

  const renderProjectDetails = (
    retailer: Retailer,
    project: Project,
    showBackButton = false,
    useCrmChecklist = false
  ) => (
    <ProjectDetails
      project={project}
      retailer={retailer}
      retailers={retailers}
      suppliers={suppliers}
      users={accountManagers}
      retailerEmails={retailerEmails}
      emailsLoading={retailerEmailsLoading}
      snapshotLoading={Boolean(aiLoadingByAction[`PROJECT_SNAPSHOT:${project._id || project.title}`])}
      showBackButton={showBackButton}
      useCrmChecklist={useCrmChecklist}
      onBack={() => setActiveProjectForTimeline(null)}
      onAddTodo={(stage, task) =>
        handleAddProjectTodo(retailer.id || retailer._id || "", project._id || "", stage, task)
      }
      onToggleTodo={(stage, idx) =>
        handleToggleProjectTodo(retailer.id || retailer._id || "", project._id || "", stage, idx)
      }
      onDeleteTodo={(stage, idx) =>
        handleDeleteProjectTodo(retailer.id || retailer._id || "", project._id || "", stage, idx)
      }
      onThreadClick={(threadId) => {
        setActiveConversationId(threadId);
        setIsConversationDrawerOpen(true);
      }}
      onToggleHotMemoryPin={(id, type) =>
        hotMemoryUserId &&
        dispatch(toggleHotMemoryPin({ userId: hotMemoryUserId, id, type: type as HotMemoryItemType }))
      }
      onRemoveHotMemory={(id, type) =>
        hotMemoryUserId &&
        dispatch(removeHotMemoryItem({ userId: hotMemoryUserId, id, type: type as HotMemoryItemType }))
      }
      onClearHotMemory={() =>
        hotMemoryUserId && dispatch(clearHotMemory({ userId: hotMemoryUserId }))
      }
      hotMemoryItems={hotMemoryItems}
      getContactDisplayName={getContactDisplayName}
      aiWarningsByAction={aiWarningsByAction}
      getSnapshotPayload={getSnapshotPayload}
      onUpdateProject={(updates) => {
        const retailerId = retailer.id || retailer._id || "";
        if (!retailerId || !project._id) return;
        updateProjectInState(retailerId, project._id, updates);
        void updateProject(retailerId, project._id, updates)
          .then((data) => {
            const updated = { ...project, ...data, ...updates } as Project;
            void forceProjectSnapshot(retailer, updated);
          })
          .catch(() => {
            message.error("Erreur lors de la mise à jour");
          });
      }}
      updateDraftCrmChecklist={updateDraftCrmChecklist}
      handleUpdateRetailer={handleUpdateRetailer}
      toFormValues={toFormValues}
    />
  );

  const renderProjectsTab = (retailer: Retailer) => {
    const projects = retailer.projects || [];
    const retailerId = retailer.id || retailer._id || '';
    
    const filteredProjects = projects.filter(p => {
      if (p.status === 'archived') return false;
      const matchesSearch = !projectSearch || 
        p.title.toLowerCase().includes(projectSearch.toLowerCase()) ||
        p.description?.toLowerCase().includes(projectSearch.toLowerCase()) ||
        p.attachedEmailIds?.some(id => {
          const mail = retailerEmails.find(m => m.id === id);
          return mail?.subject?.toLowerCase().includes(projectSearch.toLowerCase()) ||
                 mail?.preview?.toLowerCase().includes(projectSearch.toLowerCase());
        });
      
      const projectPeople = new Set(p.attachedEmailIds?.flatMap(id => {
        const mail = retailerEmails.find(m => m.id === id);
        return mail?.from ? [mail.from.toLowerCase()] : [];
      }));
      
      const matchesPeople = selectedPeopleFilters.length === 0 || 
        selectedPeopleFilters.some(f => projectPeople.has(f.toLowerCase()));

      return matchesSearch && matchesPeople;
    });

    const kanbanData = {
      todo: filteredProjects.filter(p => p.status === 'open' && p.pushPullStatus === 'push'),
      in_progress: filteredProjects.filter(p => p.status === 'open' && p.pushPullStatus === 'pull'),
      closed: filteredProjects.filter(p => p.status === 'completed' || p.status === 'cancelled' || p.status === 'on_hold')
    };

    const allProjectPeople = Array.from(new Set(projects.flatMap(p => 
      p.attachedEmailIds?.flatMap(id => {
        const mail = retailerEmails.find(m => m.id === id);
        return mail?.from ? [mail.from] : [];
      }) || []
    ))).filter(Boolean);

    const attachedEmailIds = new Set(projects.flatMap((project) => project.attachedEmailIds || []));
    const conversationBuckets = retailerEmails.reduce((acc, email) => {
      const conversationKey = email.conversationId?.trim();
      const key = conversationKey || email.id || '';
      if (!key) return acc;
      if (!acc[key]) acc[key] = [];
      acc[key].push(email);
      return acc;
    }, {} as Record<string, RetailerMailMessage[]>);

    const unassignedList = Object.entries(conversationBuckets)
      .filter(([, messages]) => messages.every((message) => !attachedEmailIds.has(message.id || '')))
      .map(([conversationId, messages]) => {
        const sorted = [...messages].sort(
          (a, b) => new Date(b.receivedDateTime || '').getTime() - new Date(a.receivedDateTime || '').getTime()
        );
        return {
          conversationId,
          latest: sorted[0],
          count: messages.length,
          unread: messages.some((message) => !message.isRead),
          ids: messages.map((message) => message.id).filter(Boolean) as string[],
        };
      })
      .sort((a, b) => new Date(b.latest.receivedDateTime || '').getTime() - new Date(a.latest.receivedDateTime || '').getTime());

    const handleCardClick = (projectId: string) => {
      setActiveProjectForTimeline(projectId);
    };

    const handleAttachConversationsToProject = async () => {
      if (!selectedEmailsForProject.length) {
        message.warning("Sélectionnez une conversation");
        return;
      }

      const targetProjectId = selectedProjectForAttach || getOnboardingProject(projects)?._id;
      if (!targetProjectId) {
        message.warning("Sélectionnez un projet cible");
        return;
      }
      const targetProject = projects.find((project) => project._id === targetProjectId);
      if (!targetProject) {
        message.warning("Projet introuvable");
        return;
      }

      const previousAttached = targetProject.attachedEmailIds || [];
      const merged = Array.from(new Set([...previousAttached, ...selectedEmailsForProject]));
      updateProjectInState(retailerId, targetProjectId, { attachedEmailIds: merged });

      try {
        await updateProject(retailerId, targetProjectId, { attachedEmailIds: merged });
        message.success("Conversations rattachées au projet");
        setSelectedEmailsForProject([]);
        setSelectedProjectForAttach(targetProjectId);
      } catch (error) {
        updateProjectInState(retailerId, targetProjectId, { attachedEmailIds: previousAttached });
        message.error("Erreur lors du rattachement");
      }
    };

    const toggleConversationSelection = (ids: string[]) => {
      setSelectedEmailsForProject((prev) => {
        const allSelected = ids.every((id) => prev.includes(id));
        if (allSelected) {
          return prev.filter((id) => !ids.includes(id));
        }
        return Array.from(new Set([...prev, ...ids]));
      });
    };

    const activeProject = projects.find((project) => project._id === activeProjectForTimeline) || null;

  const ProjectCard: React.FC<{ project: Project }> = ({ project }) => {
    const isPush = project.pushPullStatus === 'push';
    const isActive = activeProjectForTimeline === project._id;
    const progress = (() => {
      const total = project.attachedEmailIds?.length || 0;
      if (total === 0) return 0;
      return Math.min(100, (total / 5) * 100);
    })();

    const statusColor = project.priorityFlag === 'red' ? '#ef4444' : project.priorityFlag === 'yellow' ? '#f59e0b' : '#10b981';

    return (
      <div
        key={project._id}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('projectId', project._id || '');
        }}
        className={`operational-project-card ${isActive ? 'active' : ''}`}
        onClick={() => handleCardClick(project._id!)}
        style={{ 
          border: '1.6px solid #0f172a',
          borderLeft: `8px solid ${statusColor}`,
          borderRadius: '10px',
          marginBottom: '12px',
          marginTop: '12px',
          background: isActive ? '#f0f7ff' : '#ffffff',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
        }}
      >
        <div style={{ padding: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <Typography.Text strong style={{ fontSize: 13, color: '#1e293b', flex: 1, marginRight: 8 }}>{project.title}</Typography.Text>
            <Tag color={isPush ? 'error' : 'processing'} style={{ margin: 0, fontSize: 10, borderRadius: 4, padding: '0 4px' }}>
              {project.pushPullStatus?.toUpperCase()}
            </Tag>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <ClockCircleOutlined style={{ fontSize: 10, color: '#94a3b8' }} />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {project.lastEmailAt ? dayjs(project.lastEmailAt).fromNow() : 'Pas d\'activité'}
              </Typography.Text>
            </div>
            
            <div className="progress-container" style={{ background: '#f1f5f9', borderRadius: 4, padding: '6px 8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Typography.Text style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>ACTIVITÉ</Typography.Text>
                <Typography.Text style={{ fontSize: 10, fontWeight: 700, color: '#3b82f6' }}>
                  {project.unreadEmailCount || 0} non lus
                </Typography.Text>
              </div>
              <Progress
                percent={progress}
                size="small"
                showInfo={false}
                strokeColor="#3b82f6"
                style={{ margin: 0 }}
              />
            </div>
          </div>


          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography.Text type="secondary" style={{ fontSize: 9, textTransform: 'uppercase' }}>INTERLOCUTEUR</Typography.Text>
              <Typography.Text strong style={{ fontSize: 11, color: '#0f172a' }} ellipsis>
                {getContactDisplayName(retailer, project.lastEmailFrom)}
              </Typography.Text>
            </div>
            
            <Space size={4}>
              {project.priority === 'high' && (
                <Tag color="error" icon={<ThunderboltOutlined style={{ fontSize: 9 }} />} style={{ margin: 0, fontSize: 10, borderRadius: 4 }}>
                  URGENT
                </Tag>
              )}
              <Button 
                danger 
                type="text" 
                size="small"
                icon={<DeleteOutlined />} 
                onClick={(e) => { e.stopPropagation(); handleDeleteProject(project._id!); }}
                style={{ fontSize: 10, padding: '0 4px' }}
              >
                Supprimer
              </Button>
            </Space>
          </div>
        </div>
      </div>
    );
  };

    return (
      <div className="projects-view">
        {!activeProject ? (
          <>
            <div className="view-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
              <div className="header-info-group">
                <Title level={3} style={{ margin: 0, fontWeight: 800, color: '#1e293b' }}>Projets Opérationnels</Title>
                <Text type="secondary" style={{ fontSize: 14 }}>Gérez vos flux et conversations en cours</Text>
              </div>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                size="large"
                style={{ borderRadius: '12px', height: '48px', padding: '0 24px', fontWeight: 600 }}
                onClick={() => {
                  setEditingProject(null);
                  projectForm.resetFields();
                  setIsProjectModalOpen(true);
                }}
              >
                Nouveau Sujet
              </Button>
            </div>

            <div className="header-controls" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1 }}>
                  <Input
                    placeholder="Rechercher..."
                    prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                    className="premium-search"
                    value={projectSearch}
                    onChange={e => setProjectSearch(e.target.value)}
                  />
                  <div className="people-filters" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {allProjectPeople.map(person => {
                      const name = person.split('@')[0].split('.').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
                      const isActive = selectedPeopleFilters.includes(person);
                      return (
                        <div 
                          key={person} 
                          className={`filter-pill ${isActive ? 'active' : ''}`}
                          onClick={() => setSelectedPeopleFilters(prev => 
                            isActive ? prev.filter(p => p !== person) : [...prev, person]
                          )}
                          style={{
                            height: '32px',
                            padding: '0 12px',
                            borderRadius: '10px',
                            border: '1px solid #e2e8f0',
                            minWidth: 'fit-content',
                            maxWidth: '160px',
                            background: isActive ? '#3b82f6' : '#ffffff',
                            color: isActive ? '#ffffff' : '#64748b',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '12px',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {name}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {unassignedList.length > 0 && (
                  <Badge count={unassignedList.length} offset={[10, 0]}>
                    <Button 
                      icon={<MailOutlined />} 
                      onClick={() => setIsUnassignedDrawerOpen(true)} 
                      style={{ borderRadius: '10px' }}
                    >
                      Mails orphelins
                    </Button>
                  </Badge>
                )}
              </div>
            </div>

            <Row gutter={20} className="kanban-columns" style={{ margin: 0 }}>
              <Col span={8}>
                <div
                  className={`kanban-column todo ${projectDragOverColumn === 'todo' ? 'drag-over' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setProjectDragOverColumn('todo');
                  }}
                  onDragLeave={() => setProjectDragOverColumn(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    const projectId = e.dataTransfer.getData('projectId');
                    const project = projects.find((item) => item._id === projectId);
                    if (project) {
                      handleProjectDrop({ retailerId, project, target: 'todo' });
                    }
                    setProjectDragOverColumn(null);
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div className="title-badge" style={{ fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
                      À TRAITER
                    </div>
                    <Tag style={{ borderRadius: 10, border: 'none', background: '#e2e8f0', color: '#64748b', fontWeight: 700 }}>{kanbanData.todo.length}</Tag>
                  </div>
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {kanbanData.todo.map(p => <ProjectCard key={p._id} project={p} />)}
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div
                  className={`kanban-column in-progress ${projectDragOverColumn === 'in_progress' ? 'drag-over' : ''}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setProjectDragOverColumn('in_progress');
                  }}
                  onDragLeave={() => setProjectDragOverColumn(null)}
                  onDrop={(event) => {
                    event.preventDefault();
                    const projectId = event.dataTransfer.getData('projectId');
                    const project = projects.find((item) => item._id === projectId);
                    if (project) {
                      handleProjectDrop({ retailerId, project, target: 'in_progress' });
                    }
                    setProjectDragOverColumn(null);
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div className="title-badge" style={{ fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6' }} />
                      EN COURS
                    </div>
                    <Tag style={{ borderRadius: 10, border: 'none', background: '#e2e8f0', color: '#64748b', fontWeight: 700 }}>{kanbanData.in_progress.length}</Tag>
                  </div>
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {kanbanData.in_progress.map(p => <ProjectCard key={p._id} project={p} />)}
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div
                  className={`kanban-column closed ${projectDragOverColumn === 'closed' ? 'drag-over' : ''}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setProjectDragOverColumn('closed');
                  }}
                  onDragLeave={() => setProjectDragOverColumn(null)}
                  onDrop={(event) => {
                    event.preventDefault();
                    const projectId = event.dataTransfer.getData('projectId');
                    const project = projects.find((item) => item._id === projectId);
                    if (project) {
                      handleProjectDrop({ retailerId, project, target: 'closed' });
                    }
                    setProjectDragOverColumn(null);
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div className="title-badge" style={{ fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#94a3b8' }} />
                      CLOS
                    </div>
                    <Tag style={{ borderRadius: 10, border: 'none', background: '#e2e8f0', color: '#64748b', fontWeight: 700 }}>{kanbanData.closed.length}</Tag>
                  </div>
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {kanbanData.closed.map(p => <ProjectCard key={p._id} project={p} />)}
                  </div>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          renderProjectDetails(retailer, activeProject, true)
        )}

        {threadDrawer}
        <Modal
          title={editingProject ? "Modifier le Sujet" : "Nouveau Sujet"}
          open={isProjectModalOpen}
          onCancel={() => setIsProjectModalOpen(false)}
          onOk={() => projectForm.submit()}
          width={600}
        >
          <Form form={projectForm} layout="vertical" onFinish={handleSaveProject}>
            <Form.Item name="title" label="Titre du sujet" rules={[{ required: true, message: 'Le titre est requis' }]}>
              <Input placeholder="Ex: Réassort gamme hiver" />
            </Form.Item>
            <Form.Item name="description" label="Notes internes">
              <Input.TextArea rows={3} placeholder="Détails additionnels sur ce sujet..." />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="status" label="État" initialValue="open">
                  <Select options={[
                    { value: 'open', label: 'En cours' },
                    { value: 'completed', label: 'Terminé / Clos' },
                    { value: 'on_hold', label: 'En attente (On Hold)' },
                    { value: 'cancelled', label: 'Annulé' },
                  ]} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="priority" label="Urgence" initialValue="medium">
                  <Select options={[
                    { value: 'low', label: 'Basse' },
                    { value: 'medium', label: 'Normale' },
                    { value: 'high', label: 'Critique' },
                  ]} />
                </Form.Item>
              </Col>
            </Row>
            {selectedEmailsForProject.length > 0 && (
              <Alert 
                type="info" 
                showIcon 
                message={`${selectedEmailsForProject.length} conversation(s) seront rattachées`}
                style={{ marginTop: 16 }}
              />
            )}
          </Form>
        </Modal>

        {/* Drawer pour les mails orphelins */}
        <Drawer
          title="Conversations non associées"
          placement="right"
          width={400}
          onClose={() => setIsUnassignedDrawerOpen(false)}
          open={isUnassignedDrawerOpen}
          extra={
            <Space>
              <Select
                size="small"
                style={{ width: 220 }}
                placeholder="Sélectionner un projet"
                value={selectedProjectForAttach || undefined}
                onChange={(value) => setSelectedProjectForAttach(value)}
                options={projects
                  .filter((project) => project._id)
                  .map((project) => ({
                    value: project._id as string,
                    label: project.title,
                  }))}
              />
              <Button
                size="small"
                type="primary"
                disabled={selectedEmailsForProject.length === 0}
                onClick={handleAttachConversationsToProject}
              >
                Lier au projet
              </Button>
            </Space>
          }
        >
          <div className="unassigned-strip" style={{ border: 'none', background: 'transparent', padding: 0, marginTop: 0 }}>
            <div className="strip-items" style={{ gridTemplateColumns: '1fr' }}>
              {unassignedList.map((thread) => {
                const isSelected = thread.ids.every((id) => selectedEmailsForProject.includes(id));
                return (
                  <div
                    key={thread.conversationId}
                    className={`strip-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleConversationSelection(thread.ids)}
                    style={{ marginBottom: 12, position: 'relative' }}
                  >
                    <Button 
                      size="small" 
                      type="text" 
                      icon={<MessageOutlined />} 
                      style={{ position: 'absolute', right: 4, top: 4, zIndex: 10 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveConversationId(thread.conversationId);
                        setIsConversationDrawerOpen(true);
                      }}
                    />
                    <div className="strip-title" style={{ fontSize: 13, marginBottom: 6, paddingRight: 24 }}>{thread.latest.subject || '(Sans objet)'}</div>
                    <div className="strip-meta">
                      <Space>
                        <Avatar size="small" style={{ fontSize: 10 }}>{thread.latest.from?.charAt(0).toUpperCase()}</Avatar>
                        <span style={{ fontWeight: 600 }}>{thread.latest.from?.split('@')[0]}</span>
                      </Space>
                      <span>{dayjs(thread.latest.receivedDateTime).fromNow()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Drawer>
      </div>
    );
  };
  useEffect(() => {
    const fetchRetailersData = async () => {
      try {
        setLoading(true);
        const data = await getRetailers({ search, segment });
        setRetailers(data);
      } finally {
        setLoading(false);
      }
    };
    fetchRetailersData();
  }, [search, segment]);

  useEffect(() => {
    if (activeMainTab !== "network") return;
    const fetchAlerts = async () => {
      try {
        const data = await getRetailerMailAlerts();
        setMailAlerts((data || {}) as Record<string, RetailerMailAlert>);
      } catch (error) {
        message.error("Erreur lors du chargement des alertes emails");
      }
    };
    fetchAlerts();
  }, [activeMainTab]);

  const channelOptions = useMemo(
    () => [
      { value: "all", label: "Tous" },
      { value: "pos", label: "POS" },
      { value: "ecom", label: "Ecom" },
      { value: "marketplace", label: "Marketplace" },
      { value: "department_store", label: "Dept. store" },
      { value: "wholesale", label: "Wholesale" },
    ],
    []
  );

  const countryOptions = useMemo(() => {
    const countries = new Set<string>();
    retailers.forEach((retailer) => {
      (retailer.channelsDetails || []).forEach((channel) => {
        channel.coverage?.countries?.forEach((country) => countries.add(country));
        if (channel.location?.country) countries.add(channel.location.country);
      });
    });
    return [
      { value: "all", label: "Tous" },
      ...Array.from(countries)
        .filter(Boolean)
        .sort()
        .map((country) => ({ value: country, label: country })),
    ];
  }, [retailers]);

  const exportRetailerOptions = useMemo(
    () =>
      retailers.map((retailer) => ({
        value: retailer.id || retailer._id || retailer.name,
        label: retailer.name,
      })),
    [retailers]
  );

  const retailerOptions = useMemo(
    () => [
      { value: "all", label: "Tous" },
      ...retailers.map((retailer) => ({
        value: retailer.id || retailer._id || retailer.name,
        label: retailer.name,
      })),
    ],
    [retailers]
  );

  const crmBrandOptions = useMemo(() => {
    const brands = new Set<string>();
    retailers.forEach((retailer) => {
      if (retailer.crm?.proposedBrand) brands.add(retailer.crm.proposedBrand);
    });
    return Array.from(brands).sort().map((brand) => ({ value: brand, label: brand }));
  }, [retailers]);

  const crmLeadSourceOptions = useMemo(() => {
    const sources = new Set<string>();
    retailers.forEach((retailer) => {
      if (retailer.crm?.leadSource) sources.add(retailer.crm.leadSource);
    });
    return Array.from(sources).sort().map((source) => ({ value: source, label: source }));
  }, [retailers]);

  const crmProductOptions = useMemo(() => {
    const products = new Set<string>();
    retailers.forEach((retailer) => {
      (retailer.performance?.sellOut?.bestSkus || []).forEach((sku) => sku && products.add(sku));
      (retailer.performance?.sellOut?.worstSkus || []).forEach((sku) => sku && products.add(sku));
    });
    return Array.from(products).sort().map((sku) => ({ value: sku, label: sku }));
  }, [retailers]);

  const filteredRetailers = useMemo(() => {
    return retailers.filter((r) => {
      // Filtre de recherche global
      const matchesSearch =
        !search ||
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        (r.group && r.group.toLowerCase().includes(search.toLowerCase()));
      
      const matchesSegment = !segment || r.segment === segment;

      if (!matchesSearch || !matchesSegment) return false;

      // Logique spécifique par onglet
      if (activeMainTab === "network") {
        return r.status === "active";
      }

      if (activeMainTab.startsWith("crm:")) {
        if (getCrmKey(r) !== activeCrmKey) return false;
        const stage = getCrmColumnKey(r);
        const priority = r.crm?.onboardingPriority || "medium";
        const matchesStage = !crmStageFilter || stage === crmStageFilter;
        const matchesPriority = !crmPriorityFilter || priority === crmPriorityFilter;
        const matchesBrand = !crmBrandFilter || r.crm?.proposedBrand === crmBrandFilter;
        const matchesLeadSource = !crmLeadSourceFilter || r.crm?.leadSource === crmLeadSourceFilter;
        const bestSkus = r.performance?.sellOut?.bestSkus || [];
        const worstSkus = r.performance?.sellOut?.worstSkus || [];
        const matchesProduct = !crmProductFilter || bestSkus.includes(crmProductFilter) || worstSkus.includes(crmProductFilter);
        const hasEshop = r.kyc?.hasEshop;
        const matchesEshop =
          !crmHasEshopFilter ||
          (crmHasEshopFilter === "yes" ? Boolean(hasEshop) : hasEshop === false);
        if (!matchesStage || !matchesPriority || !matchesBrand || !matchesLeadSource || !matchesProduct || !matchesEshop) {
          return false;
        }
      }

      // Pour l'onglet CRM, on affiche tout le monde filtré par colonnes + filtres
      return true;
    });
  }, [
    retailers,
    search,
    segment,
    activeMainTab,
    crmStageFilter,
    crmPriorityFilter,
    crmBrandFilter,
    crmLeadSourceFilter,
    crmProductFilter,
    crmHasEshopFilter,
    activeCrmKey,
    crmTabs,
  ]);

  const exportRetailers = useMemo(() => {
    const normalizedExportCountry = normalizeKey(exportCountry);
    return filteredRetailers.filter((retailer) => {
      if (exportRetailersSelection.length > 0) {
        const retailerId = retailer.id || retailer._id || retailer.name;
        if (!exportRetailersSelection.includes(retailerId || "")) return false;
      }

      if (!exportCountry || exportCountry === "all") return true;

      const countries = new Set<string>();
      (retailer.channelsDetails || []).forEach((channel) => {
        channel.coverage?.countries?.forEach((country) => country && countries.add(country));
        if (channel.location?.country) countries.add(channel.location.country);
      });
      return Array.from(countries).some((country) => normalizeKey(country) === normalizedExportCountry);
    });
  }, [filteredRetailers, exportCountry, exportRetailersSelection]);

  const updateExportColumnSelection = (key: string, selected: boolean) => {
    setExportColumns((prev) =>
      prev.map((column) => (column.key === key ? { ...column, selected } : column))
    );
  };

  const updateExportColumnHeader = (key: string, header: string) => {
    setExportColumns((prev) =>
      prev.map((column) => (column.key === key ? { ...column, header } : column))
    );
  };

  const escapeCsvValue = (value: unknown) => {
    const text = value === null || value === undefined ? "" : String(value);
    if (/[;"\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const retailerKpisMap = useMemo(
    () => buildRetailerKpisMap(purchaseOrders),
    [purchaseOrders]
  );

  const globalRetailerKpis = useMemo(
    () => buildGlobalRetailerKpis(purchaseOrders),
    [purchaseOrders]
  );

  const handleExportCsv = () => {
    const activeColumns = exportColumns.filter((column) => column.selected);
    if (!activeColumns.length) {
      message.warning("Sélectionne au moins une colonne à exporter.");
      return;
    }
    if (!exportRetailers.length) {
      message.warning("Aucun distributeur à exporter.");
      return;
    }

    const headers = activeColumns.map((column) => escapeCsvValue(column.header || column.label));
    const rows = exportRetailers.map((retailer) =>
      activeColumns.map((column) => escapeCsvValue(column.getValue(retailer)))
    );
    const csv = [headers, ...rows].map((row) => row.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `retailers_export_${dayjs().format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setExportOpen(false);
  };

  const mapData = useMemo(() => {
    const markers: Array<{
      name: string;
      coordinates: [number, number];
      channel: string;
      offsetIndex: number;
      cityKey?: string;
      countryKey?: string;
    }> = [];
    const conqueredCountries = new Set<string>();
    const countryIntensity = new Map<string, number>();
    const markerDensity = new Map<string, number>();
    const countryStats = new Map<string, { revenue: number; distributors: Set<string> }>();

    filteredRetailers.forEach((retailer) => {
      const details: NonNullable<Retailer["channelsDetails"]> = retailer.channelsDetails || [];

      details.forEach((channel: any) => {
        if (channel.status && channel.status !== "active") return;
        const channelType = channel.type || "pos";
        if (mapChannel !== "all" && channelType !== mapChannel) return;
        
        const shouldRenderMarker = channelType === "pos" || channelType === "department_store";
        const location = channel.location;
        const coverageCountries = channel.coverage?.countries || [];
        const normalizedMapCountry = normalizeKey(mapCountry);
        
        const filteredCoverageCountries = mapCountry === "all"
          ? coverageCountries
: coverageCountries.filter((c: string) => normalizeKey(c) === normalizedMapCountry);
          
        const filteredLocationCountry = mapCountry === "all" || normalizeKey(location?.country) === normalizedMapCountry
          ? location?.country
          : undefined;

        const allCountries = [...filteredCoverageCountries, filteredLocationCountry].filter((c): c is string => Boolean(c));
        const countryMatch = mapCountry === "all" || allCountries.some(c => normalizeKey(c) === normalizedMapCountry);
        
        if (!countryMatch) return;

        if (shouldRenderMarker && (location?.city || location?.country || location?.street)) {
          const coords =
            location?.latitude !== undefined && location?.longitude !== undefined
              ? ([location.longitude, location.latitude] as [number, number])
              : resolveCoordinates(location.city, location.country);
          
          if (coords) {
            const cityKey = normalizeKey(location?.city);
            const countryKey = normalizeKey(location?.country);
            const densityKey = `${coords[0].toFixed(2)}|${coords[1].toFixed(2)}`;
            const offsetIndex = markerDensity.get(densityKey) || 0;
            markers.push({
              name: location.label || location.city || retailer.name,
              coordinates: coords,
              channel: channelType,
              offsetIndex,
              cityKey,
              countryKey,
            });
            markerDensity.set(densityKey, offsetIndex + 1);
          }
          if (location?.country) {
            const key = normalizeKey(location.country);
            if (key) conqueredCountries.add(key);
          }
        }

        const retailerId = retailer.id || retailer._id || "";
        const retailerKpis = retailerId
          ? retailerKpisMap.get(String(retailerId))
          : undefined;
        const revenue = retailerKpis?.totalAmount ?? 0;
        const normalizedCountries = Array.from(new Set(allCountries.map(c => normalizeKey(c)).filter((c): c is string => Boolean(c))));
        const revenueShare = normalizedCountries.length ? revenue / normalizedCountries.length : revenue;
        
        normalizedCountries.forEach((countryKey) => {
          const entry = countryStats.get(countryKey) || { revenue: 0, distributors: new Set<string>() };
          entry.revenue += revenueShare;
          entry.distributors.add(retailer.name);
          countryStats.set(countryKey, entry);
        });

        const fallbackCountry = mapCountry === "all"
          ? (coverageCountries[0] || location?.country)
          : (allCountries[0] || location?.country);
        
        const key = normalizeKey(fallbackCountry);
        if (key) conqueredCountries.add(key);
        
        if (channelType === "ecom" || channelType === "marketplace") {
          allCountries.forEach((country) => {
            const countryKey = normalizeKey(country);
            if (countryKey) {
              countryIntensity.set(countryKey, (countryIntensity.get(countryKey) || 0) + 1);
            }
          });
        }
      });
    });

    return { markers, conqueredCountries, countryIntensity, markerDensity, countryStats };
  }, [filteredRetailers, mapChannel, mapCountry, retailerKpisMap]);

  const mapStats = useMemo(() => {
    const distributorSet = new Set<string>();
    const countrySet = new Set<string>();
    let activeChannels = 0;
    let posChannels = 0;

    filteredRetailers.forEach((retailer) => {
      const details = retailer.channelsDetails || [];
      details.forEach((channel) => {
        if (channel.status && channel.status !== "active") return;
        const channelType = channel.type || "pos";
        if (mapChannel !== "all" && channelType !== mapChannel) return;
        
        const location = channel.location;
        const coverageCountries = channel.coverage?.countries || [];
        const normalizedMapCountry = normalizeKey(mapCountry);
        const allCountries = [...coverageCountries, location?.country].filter((c): c is string => Boolean(c));
        const countryMatch = mapCountry === "all" || allCountries.some(c => normalizeKey(c) === normalizedMapCountry);
        
        if (!countryMatch) return;

        distributorSet.add(retailer.name);
        activeChannels += 1;
        if (channelType === "pos") posChannels += 1;
        allCountries.forEach(c => countrySet.add(c));
      });
    });

    return {
      distributors: distributorSet.size,
      activeChannels,
      posChannels,
      countries: countrySet.size,
      points: mapData.markers.length,
    };
  }, [filteredRetailers, mapChannel, mapCountry, mapData.markers.length]);

  const mapIntensity = useMemo(() => {
    const maxCountry = Math.max(...Array.from(mapData.countryIntensity.values()), 1);
    const maxDensity = Math.max(...Array.from(mapData.markerDensity.values()), 1);
    return { maxCountry, maxDensity };
  }, [mapData.countryIntensity, mapData.markerDensity]);

  async function geocodeAddress<T extends {
    street?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
  }>(address?: T): Promise<T | undefined> {
    if (!address) return address;
    const query = buildAddressQuery(address);
    if (!query) return address;
    try {
      const res = await Nominatim.geocode({ q: query, limit: 1 });
      if (res?.[0]) {
        return {
          ...address,
          latitude: Number(res[0].lat),
          longitude: Number(res[0].lon),
        };
      }
      const cityQuery = [address.city, address.postalCode, address.country].filter(Boolean).join(", ");
      if (cityQuery) {
        const cityRes = await Nominatim.geocode({ q: cityQuery, limit: 1 });
        if (cityRes?.[0]) {
          return {
            ...address,
            latitude: Number(cityRes[0].lat),
            longitude: Number(cityRes[0].lon),
          };
        }
      }
    } catch (e) {}
    const fallback = address.city
      ? resolveCoordinates(address.city, undefined)
      : resolveCoordinates(undefined, address.country);
    if (fallback) {
      return {
        ...address,
        latitude: fallback[1],
        longitude: fallback[0],
      };
    }
    return address;
  }

  const enrichRetailerWithGeocoding = async (payload: any) => {
    const nextPayload = { ...payload };

    if (nextPayload.logistics?.hub) {
      nextPayload.logistics.hub = await geocodeAddress(nextPayload.logistics.hub);
    }

    if (Array.isArray(nextPayload.logistics?.hubs)) {
      nextPayload.logistics.hubs = await Promise.all(
        nextPayload.logistics.hubs.map(async (hub: any) => ({
          ...hub,
          address: await geocodeAddress(hub.address),
        }))
      );
    }

    if (Array.isArray(nextPayload.channelsDetails)) {
      nextPayload.channelsDetails = await Promise.all(
        nextPayload.channelsDetails.map(async (channel: any) => ({
          ...channel,
          location: await geocodeAddress(channel.location),
          logistics: channel.logistics
            ? {
                ...channel.logistics,
                hub: await geocodeAddress(channel.logistics.hub),
              }
            : channel.logistics,
        }))
      );
    }

    return nextPayload;
  };

  const handleCreateRetailer = async (values: any) => {
    try {
      setCreating(true);
      const payload = await enrichRetailerWithGeocoding(
        normalizePayload({
          ...values,
          crm: {
            ...(values.crm || {}),
            onboardingStage: "launched",
          },
        })
      );
      const created = await createRetailer(payload);
      message.success("Distributeur créé");
      setCreateDrawerOpen(false);
      createForm.resetFields();
      setCreateFlow(null);
      setSelected(created);
      setDrawerOpen(true);
      const data = await getRetailers({ search, segment });
      setRetailers(data);
    } catch (error) {
      message.error("Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handleCreateOnboardingRetailer = async (values: any) => {
    try {
      setCreating(true);
      const formValues = createOnboardingForm.getFieldsValue(true);
      const mergedValues = {
        ...formValues,
        ...values,
        crm: {
          ...(formValues.crm || {}),
          ...(values.crm || {}),
        },
        ...(canAssignAccountManager
          ? { accountManager: values.accountManager || formValues.accountManager }
          : {}),
        contacts: values.contacts || formValues.contacts || draftContacts,
      };
      const defaultTasks = DEFAULT_STAGE_TASKS.lead.map((task) => ({ task, completed: false }));
      const leadChecklist = mergedValues.crm?.checklists?.lead?.length
        ? mergedValues.crm.checklists.lead
        : defaultTasks;
      const payload = await enrichRetailerWithGeocoding(
        normalizePayload({
          ...mergedValues,
          contacts: mergedValues.contacts || draftContacts,
          status: "prospect",
          crm: {
            ...(mergedValues.crm || {}),
            key: activeCrmKey,
            columnKey: activeCrmColumns[0]?.key || "lead",
            onboardingStage: "lead",
            checklists: { lead: leadChecklist },
          },
        })
      );
      const created = await createRetailer(payload);
      message.success("Distributeur créé");
      setCreateDrawerOpen(false);
      createOnboardingForm.resetFields();
      setCreateFlow(null);
      setDraftContacts([]);
      setContactDrafts({});
      setContactEditing({});
      setNewContactDraft({
        name: "",
        role: "",
        email: "",
        phone: "",
        isDecisionMaker: false,
        contactType: "general",
      });
      setSelected(created);
      setDrawerOpen(true);
      const data = await getRetailers({ search, segment });
      setRetailers(data);
    } catch (error) {
      message.error("Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRetailer = async () => {
    if (!selected?.id && !selected?._id) return;
    if (!window.confirm("Supprimer ce distributeur ?")) return;
    try {
      await deleteRetailer(selected.id || selected._id || "");
      message.success("Distributeur supprimé");
      setDrawerOpen(false);
      setSelected(null);
      const data = await getRetailers({ search, segment });
      setRetailers(data);
    } catch (error) {
      message.error("Erreur lors de la suppression");
    }
  };

  const handleUpdateContacts = async (retailer: Retailer, contacts: any[]) => {
    if (!retailer.id && !retailer._id) {
      setDraftContacts(contacts);
      createOnboardingForm.setFieldsValue({ contacts });
      return;
    }
    try {
      const updatedRetailer: Retailer = {
        ...retailer,
        crm: retailer.crm ? {
          ...retailer.crm,
          onboardingStage: (retailer.crm.onboardingStage || "lead") as "lead" | "discovery" | "qualified" | "negotiation" | "contract" | "onboarding" | "launched" | "lost"
        } : undefined,
        contacts
      };
      const formValues = toFormValues(updatedRetailer);
      await handleUpdateRetailer(formValues, retailer.id || retailer._id);
      setSelected(updatedRetailer);
    } catch (error) {
      message.error("Erreur lors de la mise à jour des contacts");
    }
  };

  const renderContactsSection = (retailer: Retailer, showTitle = true) => {
    const contacts = retailer.contacts || [];
    const emptyContact = { name: "", role: "", email: "", phone: "", isDecisionMaker: false, contactType: "general" as "general" | "accounting" };
    const updateDraft = (idx: number, patch: Partial<typeof emptyContact>) => {
      setContactDrafts((prev) => ({
        ...prev,
        [idx]: { ...(prev[idx] || contacts[idx] || emptyContact), ...patch },
      }));
    };
    const resetDraft = (idx: number) => {
      setContactDrafts((prev) => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
    };
    
    return (
      <div className="contacts-section">
        {showTitle && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Title level={5} style={{ margin: 0 }}>Contacts & Décideurs</Title>
            <Button
              size="small"
              type="dashed"
              onClick={() =>
                setNewContactDraft((prev) => ({
                  ...prev,
                  contactType: "accounting",
                  role: prev.role || "Comptabilité",
                }))
              }
            >
              Préparer contact compta
            </Button>
          </div>
        )}

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card
              className="contact-card"
              size="small"
              style={{ borderRadius: 12, border: '1px dashed #cbd5f5' }}
              title={<Text strong>Ajouter un contact</Text>}
              extra={
                <Button
                  type="text"
                  size="small"
                  className="bg-[#0F172A] text-white"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    if (!newContactDraft.name && !newContactDraft.email) return;
                    const newContacts = [...contacts, newContactDraft];
                    handleUpdateContacts(retailer, newContacts);
                    setNewContactDraft(emptyContact);
                  }}
                >
                  Ajouter
                </Button>
              }
            >
              <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                Type “Comptabilité” = contact spécifique utilisé pour les relances factures.
              </Text>
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <Input
                  placeholder="Nom complet"
                  value={newContactDraft.name}
                  onChange={(e) => setNewContactDraft((prev) => ({ ...prev, name: e.target.value }))}
                />
                <Input
                  placeholder="Poste / Rôle"
                  value={newContactDraft.role}
                  onChange={(e) => setNewContactDraft((prev) => ({ ...prev, role: e.target.value }))}
                />
                <Input
                  placeholder="email@example.com"
                  value={newContactDraft.email}
                  onChange={(e) => setNewContactDraft((prev) => ({ ...prev, email: e.target.value }))}
                />
                <Input
                  placeholder="+33 6 ..."
                  value={newContactDraft.phone}
                  onChange={(e) => setNewContactDraft((prev) => ({ ...prev, phone: e.target.value }))}
                />
                <Select
                  placeholder="Type de contact"
                  value={newContactDraft.contactType}
                  onChange={(value) =>
                    setNewContactDraft((prev) => ({ ...prev, contactType: value as "general" | "accounting" }))
                  }
                  options={[
                    { value: "general", label: "Contact général" },
                    { value: "accounting", label: "Contact comptabilité" },
                  ]}
                />
              </Space>
            </Card>
          </Col>
          {contacts.length === 0 ? (
            <Col span={24}>
              <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f8fafc', border: '1px dashed #e2e8f0', borderRadius: 12 }}>
                <TeamOutlined style={{ fontSize: 32, color: '#94a3b8', marginBottom: 12 }} />
                <div style={{ color: '#64748b' }}>Aucun contact enregistré pour ce distributeur.</div>
              </div>
            </Col>
          ) : (
            contacts.map((contact, idx) => (
              <Col span={12} key={idx}>
                {!contactEditing[idx] ? (
                  <Card
                    className="contact-card"
                    size="small"
                    style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                    title={<Text strong>{contact.name || "Contact"}</Text>}
                    extra={
                      <Space>
                        {contact.isDecisionMaker && <Tag color="gold">Décideur</Tag>}
                        {contact.contactType === "accounting" && <Tag color="blue">Comptabilité</Tag>}
                        <Button
                          size="small"
                          onClick={() => {
                            setContactEditing((prev) => ({ ...prev, [idx]: true }));
                            setContactDrafts((prev) => ({ ...prev, [idx]: contact }));
                          }}
                        >
                          Éditer
                        </Button>
                      </Space>
                    }
                    actions={[
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          const newContacts = contacts.filter((_, i) => i !== idx);
                          handleUpdateContacts(retailer, newContacts);
                        }}
                      >
                        Supprimer
                      </Button>,
                    ]}
                  >
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">{contact.role || "Poste non précisé"}</Text>
                      <Text>{contact.email || "Email non renseigné"}</Text>
                      <Text>{contact.phone || "Téléphone non renseigné"}</Text>
                      <Text type="secondary">
                        {contact.contactType === "accounting" ? "Type: Comptabilité" : "Type: Général"}
                      </Text>
                    </Space>
                  </Card>
                ) : (
                <Card 
                  className="contact-card"
                  size="small"
                  style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                  actions={[
                    <Button
                      key="save"
                      type="primary"
                      size="small"
                      onClick={() => {
                        const draft = contactDrafts[idx] || contact;
                        const newContacts = [...contacts];
                        newContacts[idx] = draft;
                        handleUpdateContacts(retailer, newContacts);
                        setContactEditing((prev) => ({ ...prev, [idx]: false }));
                        resetDraft(idx);
                      }}
                    >
                      Enregistrer
                    </Button>,
                    <Button
                      key="cancel"
                      size="small"
                      onClick={() => {
                        setContactEditing((prev) => ({ ...prev, [idx]: false }));
                        resetDraft(idx);
                      }}
                    >
                      Annuler
                    </Button>,
                    <Button 
                      key="delete"
                      type="text" 
                      danger 
                      icon={<DeleteOutlined />} 
                      onClick={() => {
                        const newContacts = contacts.filter((_, i) => i !== idx);
                        handleUpdateContacts(retailer, newContacts);
                      }}
                    >
                      Supprimer
                    </Button>,
                    <Space>
                      <Text style={{ fontSize: 12 }}>Décideur</Text>
                      <Select 
                        size="small"
                        value={(contactDrafts[idx]?.isDecisionMaker ?? contact.isDecisionMaker) ? "yes" : "no"}
                        onChange={(val) => {
                          updateDraft(idx, { isDecisionMaker: val === "yes" });
                        }}
                        options={[
                          { value: "yes", label: "Oui" },
                          { value: "no", label: "Non" }
                        ]}
                        style={{ width: 70 }}
                      />
                    </Space>
                  ]}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={12}>
                    <Input 
                      placeholder="Nom complet" 
                      variant="borderless"
                      style={{ fontSize: 16, fontWeight: 600, padding: 0 }}
                      value={contactDrafts[idx]?.name ?? contact.name}
                      onChange={(e) => updateDraft(idx, { name: e.target.value })}
                    />
                    <Input 
                      placeholder="Poste / Rôle" 
                      variant="borderless"
                      style={{ fontSize: 13, color: '#64748b', padding: 0 }}
                      value={contactDrafts[idx]?.role ?? contact.role}
                      onChange={(e) => updateDraft(idx, { role: e.target.value })}
                    />
                    <Divider style={{ margin: '4px 0' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MessageOutlined style={{ color: '#94a3b8', fontSize: 12 }} />
                      <Input 
                        placeholder="email@example.com" 
                        variant="borderless"
                        style={{ fontSize: 13, padding: 0 }}
                        value={contactDrafts[idx]?.email ?? contact.email}
                        onChange={(e) => updateDraft(idx, { email: e.target.value })}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <EnvironmentOutlined style={{ color: '#94a3b8', fontSize: 12 }} />
                      <Input 
                        placeholder="+33 6 ..." 
                        variant="borderless"
                        style={{ fontSize: 13, padding: 0 }}
                        value={contactDrafts[idx]?.phone ?? contact.phone}
                        onChange={(e) => updateDraft(idx, { phone: e.target.value })}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text type="secondary" style={{ minWidth: 82 }}>Type</Text>
                      <Select
                        size="small"
                        value={(contactDrafts[idx]?.contactType ?? contact.contactType ?? "general") as "general" | "accounting"}
                        onChange={(val) => updateDraft(idx, { contactType: val as "general" | "accounting" })}
                        options={[
                          { value: "general", label: "Général" },
                          { value: "accounting", label: "Comptabilité" },
                        ]}
                        style={{ width: 170 }}
                      />
                    </div>
                  </Space>
                </Card>
                )}
              </Col>
            ))
          )}
        </Row>
      </div>
    );
  };


  const handleDeleteProjectTodo = async (retailerId: string, projectId: string, stage: 'todo' | 'in_progress', todoIndex: number) => {
    const project = retailers.find(r => (r.id || r._id) === retailerId)?.projects?.find(p => p._id === projectId);
    if (!project) return;
    const currentTaskBoard = project.taskBoard || {};
    const stageTasks = [...(currentTaskBoard[stage] || [])];
    if (!stageTasks[todoIndex]) return;
    stageTasks.splice(todoIndex, 1);
    const nextTaskBoard = { ...currentTaskBoard, [stage]: stageTasks };
    updateProjectInState(retailerId, projectId, { taskBoard: nextTaskBoard });
    try {
      await updateProject(retailerId, projectId, { taskBoard: nextTaskBoard });
    } catch (error) {
      updateProjectInState(retailerId, projectId, { taskBoard: currentTaskBoard });
      message.error("Erreur lors de la suppression de la tâche");
    }
  };

  const renderKycSection = (retailer: Retailer) => {
    const isDraft = !retailer.id && !retailer._id;
    const activeScope = (isDraft ? createOnboardingScope : kycScope) || retailer.kyc?.marketScopeType || "cities";
    const hasEshop = isDraft ? createOnboardingHasEshop : kycHasEshop;
    const kycTagRender = (props: { label: React.ReactNode; closable: boolean; onClose: () => void }) => {
      const { label, closable, onClose } = props;
      return (
        <Tag color="blue" closable={closable} onClose={onClose} style={{ marginRight: 4 }}>
          {label}
        </Tag>
      );
    };
    const handleSubmitKyc = async (values: any) => {
      try {
        if (isDraft) {
          createOnboardingForm.setFieldsValue(values);
          message.success("KYC prêt pour création");
          return;
        }
        const nextKyc = { ...(retailer.kyc || {}), ...(values.kyc || {}) };
        const nextCrm = {
          ...(retailer.crm || {}),
          ...(values.crm || {}),
          onboardingStage: (retailer.crm?.onboardingStage || "lead") as "lead" | "discovery" | "qualified" | "negotiation" | "contract" | "onboarding" | "launched" | "lost",
        };
        const scope = values.kyc?.marketScopeType || nextKyc.marketScopeType || "cities";
        if (scope === "cities") {
          nextKyc.marketRegions = [];
          nextKyc.marketCountries = [];
        }
        if (scope === "regions") {
          nextKyc.marketCities = [];
          nextKyc.marketCountries = [];
        }
        if (scope === "countries") {
          nextKyc.marketCities = [];
          nextKyc.marketRegions = [];
        }

        const updatedRetailer: Retailer = {
          ...retailer,
          name: values.name ?? retailer.name,
          group: values.group ?? retailer.group,
          segment: values.segment ?? retailer.segment,
          crm: nextCrm,
          kyc: nextKyc,
        };
        const formValues = toFormValues(updatedRetailer);
        await handleUpdateRetailer(formValues, retailer.id || retailer._id);
        setSelected(updatedRetailer);
        message.success("KYC mis à jour");
      } catch (error) {
        message.error("Erreur lors de la mise à jour");
      }
    };

    const kycFields = (
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item name="name" label="Nom du distributeur">
            <Input placeholder="Ex: Galeries & Partners" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="segment" label="Segment retailer">
            <Select
              options={[
                { value: "A", label: "Segment A (Top Tier)" },
                { value: "B", label: "Segment B (Mid Tier)" },
                { value: "C", label: "Segment C (Niche/Long Tail)" }
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["crm", "estimatedValue"]} label="Valeur mensuelle estimée (€)">
            <InputNumber<number>
              style={{ width: "100%" }}
              min={0}
              placeholder="0"
              formatter={(value) => {
                if (value === undefined || value === null) return "";
                const valStr = String(value);
                if (valStr === "") return "";
                const numeric = Number(valStr.replace(/[^\d,.-]/g, "").replace(",", "."));
                if (Number.isNaN(numeric)) return "";
                return `€ ${new Intl.NumberFormat("fr-FR").format(numeric)}`;
              }}
              parser={(value) => {
                if (!value) return 0;
                const cleanValue = String(value).replace(/[^\d,.-]/g, "").replace(",", ".");
                return cleanValue ? Number(cleanValue) : 0;
              }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["kyc", "registrationNumber"]} label="Numéro SIREN / Reg. NR">
            <Input placeholder="Ex: 843 123 456" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["kyc", "annualRevenue"]} label="CA annuel (€)">
            <InputNumber<number>
              style={{ width: "100%" }}
              min={0}
              placeholder="0"
              formatter={(value) => {
                if (value === undefined || value === null) return "";
                const valStr = String(value);
                if (valStr === "") return "";
                const numeric = Number(valStr.replace(/[^\d,.-]/g, "").replace(",", "."));
                if (Number.isNaN(numeric)) return "";
                return `€ ${new Intl.NumberFormat("fr-FR").format(numeric)}`;
              }}
              parser={(value) => {
                if (!value) return 0;
                const cleanValue = String(value).replace(/[^\d,.-]/g, "").replace(",", ".");
                return cleanValue ? Number(cleanValue) : 0;
              }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["kyc", "posCount"]} label="Nombre de POS">
            <Input type="number" min={0} placeholder="0" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["kyc", "marketScopeType"]} label="Zone ciblée">
            <Select
              options={[
                { value: "cities", label: "Villes" },
                { value: "regions", label: "Régions" },
                { value: "countries", label: "Pays" }
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={[
              "kyc",
              activeScope === "regions"
                ? "marketRegions"
                : activeScope === "countries"
                ? "marketCountries"
                : "marketCities",
            ]}
            label={
              activeScope === "regions"
                ? "Régions"
                : activeScope === "countries"
                ? "Pays"
                : "Villes"
            }
          >
            <Select
              mode={activeScope === "countries" ? "multiple" : "tags"}
              options={activeScope === "countries" ? EUROPE_COUNTRY_OPTIONS : undefined}
              tagRender={kycTagRender}
              placeholder={
                activeScope === "regions"
                  ? "Ajoutez des régions"
                  : activeScope === "countries"
                  ? "Ajoutez des pays"
                  : "Ajoutez des villes"
              }
              showSearch
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name={["kyc", "hasEshop"]} label="E-shop" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        {hasEshop && (
          <Col span={24}>
            <Form.Item name={["kyc", "eshopUrl"]} label="URL e-shop">
              <Input placeholder="https://www.exemple.com" />
            </Form.Item>
          </Col>
        )}
        <Col span={24}>
          <Form.Item name={["kyc", "notes"]} label="Notes">
            <Input.TextArea rows={2} placeholder="Notes / observations..." />
          </Form.Item>
        </Col>
      </Row>
    );

    return (
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Title level={5} style={{ margin: 0 }}>Dossier Administratif & KYC</Title>
        <Card 
          className="glass-card" 
          style={{ border: '1px solid #e2e8f0', borderRadius: 12 }}
        >
          {isDraft ? (
            kycFields
          ) : (
            <Form form={kycForm} layout="vertical" onFinish={handleSubmitKyc}>
              {kycFields}
              <Space>
                <Button type="primary" htmlType="submit">
                  Valider les modifications
                </Button>
              </Space>
            </Form>
          )}
        </Card>
      </Space>
    );
  };

  const renderPostOnboardingDrawer = (retailer: Retailer) => {
    const alertCount = selected ? (mailAlerts[selected.id || selected._id || ""]?.count || 0) : 0;
    const unreadIncoming = selected
      ? retailerEmails
          .filter(m => !m.isRead)
          .filter(m => !(m.mailboxIdentity && m.mailboxIdentity === m.from))
      : [];
    const retailerId = retailer.id || retailer._id || "";
    const retailerKpis = retailerId
      ? retailerKpisMap.get(String(retailerId))
      : undefined;
    return (
      <Tabs
        activeKey={activeDrawerTab}
        onChange={setActiveDrawerTab}
        className="neo-drawer-tabs"
        items={[
          {
            key: "overview",
            label: <span><FundOutlined /> Synthèse</span>,
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size={24}>
                {(unreadIncoming.length !== 0 && alertCount !== 0) && (
                <div className="premium-mail-container">
                    <div className="panel-section-title" style={{ marginBottom: 20 }}>
                      <Title level={3} style={{ margin: 0, fontWeight: 800, marginTop: 20 }}>Mails Entrants & Flux</Title>
                    </div>
                  {!!selected && (() => {
                    if (retailerEmails.length === 0 && alertCount > 0) {
                      return Array.from({ length: alertCount }).map((_, idx) => (
                        <div key={`mail-skeleton-${idx}`} className="incoming-mail-card">
                          <Skeleton active avatar paragraph={{ rows: 2 }} />
                        </div>
                      ));
                    }
                    return unreadIncoming.slice(0, 5).map(mail => {
                      const associatedProject = selected.projects?.find(p => p.attachedEmailIds?.includes(mail.id || ""));
                      const senderName = mail.from?.split('@')[0].split('.').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
                      return (
                        <div key={mail.id} className="incoming-mail-card" onClick={() => {
                          if (associatedProject) {
                            setActiveDrawerTab("projects");
                            setActiveProjectForTimeline(associatedProject._id || null);
                          }
                        }}>
                          <div className="mail-sender-avatar">
                            {mail.from?.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {associatedProject && (
                              <div className="mail-project-tag">{associatedProject.title}</div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                              <Text strong style={{ fontSize: 14 }}>{mail.subject}</Text>
                              <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(mail.receivedDateTime).fromNow()}</Text>
                            </div>
                            <div style={{ marginBottom: 4 }}>
                              <Text strong style={{ fontSize: 12, color: '#64748b' }}>{senderName}</Text>
                            </div>
                            <Typography.Paragraph 
                              type="secondary" 
                              style={{ fontSize: 12, margin: 0, height: 40 }} 
                              ellipsis={{ rows: 2 }}
                            >
                              {mail.preview}
                            </Typography.Paragraph>
                          </div>
                        </div>
                      );
                    });
                  })()}
                  {(!selected || (unreadIncoming.length === 0 && alertCount === 0)) && (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                      Aucun message récent
                    </div>
                  )}
                </div>)}
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Card className="glass-card" size="small" loading={kpiLoading}>
                      <Statistic 
                        title={(
                          <Tooltip title="Chiffre d'affaires brut des commandes du retailer (YTD)">
                            <span>Sell-in YTD</span>
                          </Tooltip>
                        )} 
                        value={retailerKpis?.totalAmount || 0}
                        prefix="€" 
                        valueStyle={{ color: '#0f172a', fontWeight: 700 }}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card className="glass-card" size="small" loading={kpiLoading}>
                      <Statistic 
                        title={(
                          <Tooltip title="Revenu net (sell-in - rétrocession - coûts import/transport)">
                            <span>Sell-out YTD</span>
                          </Tooltip>
                        )} 
                        value={retailerKpis?.netRevenue || 0}
                        prefix="€" 
                        valueStyle={{ color: '#0f172a', fontWeight: 700 }}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card className="glass-card" size="small" loading={kpiLoading}>
                      <Statistic 
                        title={(
                          <Tooltip title="OTIF : livraisons à l'heure / livraisons réalisées">
                            <span>Qualité OTIF</span>
                          </Tooltip>
                        )} 
                        value={retailerKpis?.onTimeRate || 0}
                        suffix="%" 
                        valueStyle={{ color: (retailerKpis?.onTimeRate || 0) > 90 ? '#10b981' : '#f59e0b', fontWeight: 700 }}
                      />
                    </Card>
                  </Col>
                </Row>

                  <Title level={3} style={{ margin: 0, marginTop: 20, fontWeight: 800 }}>Profil distribueur</Title>
                <Card className="glass-card">
                  <Row gutter={[24, 24]}>
                    <Col span={12}>
                      <Space direction="vertical" size={2}>
                        <Text type="secondary" style={{ fontSize: 11 }}>GROUPE / PARENT</Text>
                        <Text strong>{retailer.group || "Indépendant"}</Text>
                      </Space>
                      <Divider style={{ margin: '12px 0' }} />
                      <Space direction="vertical" size={2}>
                        <Text type="secondary" style={{ fontSize: 11 }}>SEGMENTATION</Text>
                        <Tag color="blue">Segment {retailer.segment || "C"}</Tag>
                      </Space>
                    </Col>
                    <Col span={12}>
                      <Space direction="vertical" size={2}>
                        <Text type="secondary" style={{ fontSize: 11 }}>PAYS COUVERTS</Text>
                        <Space wrap>
                          {getRetailerCountries(retailer).map((country) => (
                            <Tag key={country}>{country}</Tag>
                          ))}
                        </Space>
                      </Space>
                      <Divider style={{ margin: '12px 0' }} />
                      <Space direction="vertical" size={2}>
                        <Text type="secondary" style={{ fontSize: 11 }}>STATUT GLOBAL</Text>
                        <Badge status={retailer.status === 'active' ? 'success' : 'default'} text={retailer.status?.toUpperCase() || "ACTIVE"} />
                      </Space>
                    </Col>
                  </Row>
                </Card>

                <Card className="glass-card" title="Account Manager" style={{ marginBottom: 16 }}>
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Text style={{ fontSize: 18, fontWeight: 700 }}>
                      {retailer.accountManager?.firstName || retailer.accountManager?.lastName
                        ? `${retailer.accountManager?.firstName || ""} ${retailer.accountManager?.lastName || ""}`.trim()
                        : "Aucun account manager"}
                    </Text>
                    {retailer.accountManager?.email && (
                      <Text type="secondary">{retailer.accountManager.email}</Text>
                    )}
                    {canAssignAccountManager && (
                      <Select
                        style={{ width: "100%" }}
                        value={retailer.accountManager?.id}
                        placeholder="Sélectionner un user"
                        options={selectableAccountManagers.map((u) => ({
                          value: u._id,
                          label: `${u.firstName || ""} ${u.lastName || ""} (${u.email || ""})`,
                        }))}
                        onChange={async (userId) => {
                          if (!retailer.id && !retailer._id) return;
                          try {
                            const updated = await assignAccountManager(retailer.id || retailer._id || "", userId);
                            setSelected(updated);
                            const data = await getRetailers({ search, segment });
                            setRetailers(data);
                            message.success("Account manager assigné");
                          } catch (error) {
                            message.error("Erreur lors de l'assignation");
                          }
                        }}
                      />
                    )}
                  </Space>
                </Card>

                <Card className="glass-card" title="Notes & Gouvernance">
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>NOTES STRATÉGIQUES</Text>
                  <Text>{retailer.notes || "Aucune note particulière."}</Text>
                  {retailer.governance?.orgNotes && (
                    <>
                      <Divider style={{ margin: '16px 0' }} />
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>ORGANISATION INTERNE</Text>
                      <Text>{retailer.governance.orgNotes}</Text>
                    </>
                  )}
                </Card>
              </Space>
            ),
          },
          {
            key: "projects",
            label: <span><FolderOpenOutlined /> Projets</span>,
            children: renderProjectsTab(retailer),
          },
          {
            key: "network",
            label: <span><ShopOutlined /> Réseau & Canaux</span>,
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                {(retailer.channelsDetails || []).map((channel, idx) => (
                  <Card key={idx} className="glass-card" size="small" 
                    title={
                      <Space>
                        <Tag className={`channel-tag ${channel.type || "pos"}`}>{channel.type?.toUpperCase()}</Tag>
                        <Text strong>{channel.name || channel.key}</Text>
                      </Space>
                    }
                    extra={<Tag color={channel.status === 'active' ? 'green' : 'orange'}>{channel.status}</Tag>}
                  >
                    <Row gutter={[16, 16]}>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>COUVERTURE</Text>
                        <Text>{channel.coverage?.doors || 0} portes / {(channel.coverage?.countries || []).join(', ') || "N/A"}</Text>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>LOGISTIQUE</Text>
                        <Text>{channel.logistics?.fulfillmentMode === 'hub' ? 'Via Hub' : 'Livraison Directe'}</Text>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>LANCEMENT</Text>
                        <Text>{channel.launchedAt ? dayjs(channel.launchedAt).format('DD/MM/YYYY') : "-"}</Text>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </Space>
            )
          },
          {
            key: "operations",
            label: <span><DeploymentUnitOutlined /> Logistique & Ops</span>,
            children: (
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card className="glass-card" title="Configuration Flux">
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Mode Fulfillment">{retailer.logistics?.fulfillmentMode || "hub"}</Descriptions.Item>
                      <Descriptions.Item label="SLA (Jours)">{retailer.logistics?.slaDays || 0}</Descriptions.Item>
                      <Descriptions.Item label="Lead Time">{retailer.logistics?.leadTimeDays || 0} jours</Descriptions.Item>
                      <Descriptions.Item label="EDI Provider">{retailer.logistics?.ediProvider || "None"}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card className="glass-card" title="Hub Principal">
                    {retailer.logistics?.hub ? (
                      <Space direction="vertical" size={2}>
                        <Text strong>{retailer.logistics.hub.label}</Text>
                        <Text type="secondary">{retailer.logistics.hub.street}</Text>
                        <Text type="secondary">{retailer.logistics.hub.postalCode} {retailer.logistics.hub.city}</Text>
                        <Text type="secondary">{retailer.logistics.hub.country}</Text>
                      </Space>
                    ) : <Text italic>Aucun hub configuré</Text>}
                  </Card>
                </Col>
                <Col span={24}>
                  <Card className="glass-card" title="Notes Entrepôt / Préparation">
                    <Text>{retailer.logistics?.warehouseNotes || "Pas d'instructions spécifiques."}</Text>
                  </Card>
                </Col>
              </Row>
            )
          },
          {
            key: "commercial",
            label: <span><SafetyOutlined /> Commercial & Contrats</span>,
            children: (
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card className="glass-card" title="Conditions Financières">
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Remise Commerciale">{retailer.contracts?.discountRate || 0}%</Descriptions.Item>
                      <Descriptions.Item label="Conditions de paiement">{retailer.contracts?.paymentTerms || "Standard"}</Descriptions.Item>
                      <Descriptions.Item label="MOQ (Unités)">{retailer.contracts?.moq || 0}</Descriptions.Item>
                      <Descriptions.Item label="Incoterms">{retailer.contracts?.incoterms || "EXW"}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card className="glass-card" title="Exclusivité & Territoires">
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>STATUT EXCLUSIVITÉ</Text>
                        <Tag color={retailer.contracts?.exclusivity ? "purple" : "default"}>
                          {retailer.contracts?.exclusivity || "Pas d'exclusivité"}
                        </Tag>
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>TERRITOIRES CONTRACTUELS</Text>
                        <Space wrap>
                          {(retailer.contracts?.territories || []).map(t => <Tag key={t}>{t}</Tag>)}
                        </Space>
                      </div>
                    </Space>
                  </Card>
                </Col>
                <Col span={24}>
                  <Card className="glass-card" title="Pénalités & SLA">
                    <Text>{retailer.contracts?.penalties || "Aucune pénalité contractuelle définie."}</Text>
                  </Card>
                </Col>
              </Row>
            )
          },
          {
            key: "finance",
            label: <span><DollarOutlined /> Finance & Risque</span>,
            children: (
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card className="glass-card" title="Situation Crédit">
                    <Statistic 
                      title="Limite de crédit" 
                      value={retailer.finance?.creditLimit || 0} 
                      prefix="€" 
                      valueStyle={{ fontSize: 20 }}
                    />
                    <Divider style={{ margin: '12px 0' }} />
                    <Statistic 
                      title="Encours actuel" 
                      value={retailer.finance?.outstandingBalance || 0} 
                      prefix="€" 
                      valueStyle={{ fontSize: 20, color: (retailer.finance?.outstandingBalance || 0) > (retailer.finance?.creditLimit || 0) ? '#ef4444' : 'inherit' }}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card className="glass-card" title="Scoring Risque">
                    <Statistic 
                      title={(
                        <Tooltip title="Score interne: >60 = risque élevé; tient compte délais et incidents">
                          <span>Score Risque</span>
                        </Tooltip>
                      )} 
                      value={retailer.risk?.score || 0} 
                      suffix="/100"
                      valueStyle={{ color: (retailer.risk?.score || 0) > 60 ? '#ef4444' : '#10b981' }}
                    />
                    <Progress 
                      percent={retailer.risk?.score || 0} 
                      showInfo={false} 
                      strokeColor={(retailer.risk?.score || 0) > 60 ? '#ef4444' : '#10b981'}
                    />
                    <Divider style={{ margin: '12px 0' }} />
                    <Text type="secondary">Retards de paiement (moy.): {retailer.finance?.paymentDelayDays || 0} jours</Text>
                    <Divider style={{ margin: '12px 0' }} />
                    <Text strong>Échéance contractuelle</Text>
                    <div>
                      <Text type="secondary">
                        Base : {
                          retailer.finance?.paymentTermsPolicy?.baseDate === "invoice"
                            ? "émission de facture"
                            : retailer.finance?.paymentTermsPolicy?.baseDate === "order"
                              ? "date de commande"
                              : "réception marchandise"
                        }
                        {retailer.finance?.paymentTermsPolicy?.endOfMonth ? " • fin de mois" : ""}
                        {retailer.finance?.paymentTermsPolicy?.extraDays
                          ? ` • +${retailer.finance.paymentTermsPolicy.extraDays} jours`
                          : ""}
                      </Text>
                    </div>
                  </Card>
                </Col>
              </Row>
            )
          },
          {
            key: "contacts",
            label: <span><TeamOutlined /> Contacts</span>,
            children: renderContactsSection(retailer)
          }
        ]}
      />
    );
  };

  const renderOnboardingDrawer = (retailer: Retailer) => {
    const latestProject = getOnboardingProject(retailer.projects);
    
    // Fallback project for onboarding if no projects exist yet
    const onboardingProject = latestProject || {
      title: "Onboarding Retailer",
      status: "open",
      priority: "medium",
      attachedEmailIds: [],
    };

    const isDraft = !retailer.id && !retailer._id;
    const currentPriority = retailer.crm?.onboardingPriority || "medium";
    const currentBrand = retailer.crm?.proposedBrand;
    const handlePriorityChange = async (value: "low" | "medium" | "high") => {
      if (isDraft) {
        const currentCrm = createOnboardingForm.getFieldValue("crm") || {};
        createOnboardingForm.setFieldsValue({ crm: { ...currentCrm, onboardingPriority: value } });
        return;
      }
      try {
        const updatedRetailer: Retailer = {
          ...retailer,
          crm: {
            ...(retailer.crm || {}),
            onboardingStage: (retailer.crm?.onboardingStage || "lead") as any,
            onboardingPriority: value,
            checklists: retailer.crm?.checklists || {},
          },
        };
        const formValues = toFormValues(updatedRetailer);
        await handleUpdateRetailer(formValues, retailer.id || retailer._id);
        setSelected(updatedRetailer);
        message.success("Priorité mise à jour");
      } catch (error) {
        message.error("Erreur lors de la mise à jour de la priorité");
      }
    };
    const handleBrandChange = async (value?: string) => {
      if (isDraft) {
        const currentCrm = createOnboardingForm.getFieldValue("crm") || {};
        createOnboardingForm.setFieldsValue({ crm: { ...currentCrm, proposedBrand: value } });
        return;
      }
      try {
        const updatedRetailer: Retailer = {
          ...retailer,
          crm: {
            ...(retailer.crm || {}),
            onboardingStage: (retailer.crm?.onboardingStage || "lead") as any,
            proposedBrand: value,
            checklists: retailer.crm?.checklists || {},
          },
        };
        const formValues = toFormValues(updatedRetailer);
        await handleUpdateRetailer(formValues, retailer.id || retailer._id);
        setSelected(updatedRetailer);
        message.success("Marque proposée mise à jour");
      } catch (error) {
        message.error("Erreur lors de la mise à jour de la marque");
      }
    };
    const useCrmChecklist = !(onboardingProject as Project)._id;
    return (
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        {renderProjectDetails(retailer, onboardingProject as Project, false, useCrmChecklist)}
        {threadDrawer}
        <Col span={24}>
          {renderKycSection(retailer)}
        </Col>
        <Col span={24}>
          <Card className="glass-card" title="Relation commerciale" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                {isDraft ? (
                  <Form.Item label="Priorité onboarding" name={["crm", "onboardingPriority"]}>
                    <Select
                      style={{ width: "100%" }}
                      onChange={handlePriorityChange}
                      options={[
                        { value: "high", label: "Niveau 1" },
                        { value: "medium", label: "Niveau 2" },
                        { value: "low", label: "Niveau 3" },
                      ]}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item label="Priorité onboarding">
                    <Select
                      value={currentPriority}
                      style={{ width: "100%" }}
                      onChange={handlePriorityChange}
                      options={[
                        { value: "high", label: "Niveau 1" },
                        { value: "medium", label: "Niveau 2" },
                        { value: "low", label: "Niveau 3" },
                      ]}
                    />
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                {isDraft ? (
                  canAssignAccountManager ? (
                    <Form.Item label="Account manager" name={["accountManager", "id"]}>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Sélectionner un user"
                        options={selectableAccountManagers.map((u) => ({
                          value: u._id,
                          label: `${u.firstName || ""} ${u.lastName || ""} (${u.email || ""})`,
                        }))}
                        onChange={async (userId) => {
                          if (!retailer.id && !retailer._id) {
                            createOnboardingForm.setFieldsValue({ accountManager: { id: userId } });
                            return;
                          }
                          try {
                            const updated = await assignAccountManager(retailer.id || retailer._id || "", userId);
                            setSelected(updated);
                            const data = await getRetailers({ search, segment });
                            setRetailers(data);
                            message.success("Account manager assigné");
                          } catch (error) {
                            message.error("Erreur lors de l'assignation");
                          }
                        }}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item label="Account manager">
                      <Input disabled value={retailer.accountManager?.id || ""} placeholder="Non assigné" />
                    </Form.Item>
                  )
                ) : (
                  canAssignAccountManager ? (
                    <Form.Item label="Account manager">
                      <Select
                        style={{ width: "100%" }}
                        value={retailer.accountManager?.id}
                        placeholder="Sélectionner un user"
                        options={selectableAccountManagers.map((u) => ({
                          value: u._id,
                          label: `${u.firstName || ""} ${u.lastName || ""} (${u.email || ""})`,
                        }))}
                        onChange={async (userId) => {
                          if (!retailer.id && !retailer._id) {
                            createOnboardingForm.setFieldsValue({ accountManager: { id: userId } });
                            return;
                          }
                          try {
                            const updated = await assignAccountManager(retailer.id || retailer._id || "", userId);
                            setSelected(updated);
                            const data = await getRetailers({ search, segment });
                            setRetailers(data);
                            message.success("Account manager assigné");
                          } catch (error) {
                            message.error("Erreur lors de l'assignation");
                          }
                        }}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item label="Account manager">
                      <Input disabled value={retailer.accountManager?.id || ""} placeholder="Non assigné" />
                    </Form.Item>
                  )
                )}
              </Col>
              <Col span={24}>
                {isDraft ? (
                  <Form.Item label="Marque à proposer" name={["crm", "proposedBrand"]}>
                    <Select
                      allowClear
                      showSearch
                      placeholder="Sélectionner une marque"
                      options={supplierOptions}
                      onChange={handleBrandChange}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item label="Marque à proposer">
                    <Select
                      allowClear
                      showSearch
                      placeholder="Sélectionner une marque"
                      value={currentBrand}
                      options={supplierOptions}
                      onChange={handleBrandChange}
                    />
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Divider style={{ margin: '8px 0 16px' }} />
            {renderContactsSection(retailer, false)}
          </Card>
        </Col>
      </Space>
    );
  };

  const renderAlertTooltip = (alert?: RetailerMailAlert) => {
    if (!alert || !alert.messages?.length) return null;
    return (
      <div className="custom-mail-tooltip">
        <div className="tooltip-header">
          <Text strong style={{ color: '#0f172a' }}>Derniers messages</Text>
        </div>
        <div className="tooltip-body">
          {alert.messages.slice(0, 3).map((message) => (
            <div key={message.id || message.subject} className="tooltip-item">
              <div className="item-dot" />
              <div className="item-content">
                <Text strong style={{ fontSize: 12, display: 'block' }}>{message.subject || "(Sans objet)"}</Text>
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{message.from || "Expéditeur inconnu"}</Text>
              </div>
            </div>
          ))}
          {alert.count > 3 && (
            <div className="tooltip-footer">
              <Text type="secondary" style={{ fontSize: 11 }}>+{alert.count - 3} autres messages non lus</Text>
            </div>
          )}
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: "Distributeur",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: Retailer) => (
        <Space direction="vertical" size={0}>
          <Space size={8}>
            {record.projectsPriorityFlag && (
              <Badge 
                status={
                  record.projectsPriorityFlag === 'red' ? 'error' : 
                  record.projectsPriorityFlag === 'yellow' ? 'warning' : 'success'
                } 
                className="priority-dot"
              />
            )}
            <Text strong style={{ color: "#1a2433" }}>{text}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.group || "Indépendant"}</Text>
        </Space>
      ),
    },
    {
      title: "Segment",
      dataIndex: "segment",
      key: "segment",
      render: (v: string) => <Tag color="cyan">{v || "C"}</Tag>,
    },
    {
      title: "Valeur total",
      key: "totalValue",
      render: (_: any, record: Retailer) => {
        const retailerId = record.id || record._id || "";
        const retailerKpis = retailerId
          ? retailerKpisMap.get(String(retailerId))
          : undefined;
        return (
          <Text strong style={{ color: "#0f172a" }}>
            {formatCurrency(retailerKpis?.totalAmount ?? 0)}
          </Text>
        );
      },
    },
    {
      title: "Pays",
      dataIndex: "countries",
      key: "countries",
      render: (_: string[] | undefined, record: Retailer) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {(() => {
            const countries = getRetailerCountries(record);
            return countries.length > 0 ? countries.join(", ") : "-";
          })()}
        </Text>
      ),
    },
    {
      title: "Canaux",
      dataIndex: "channels",
      key: "channels",
      render: (_: any, record: Retailer) => {
        const channels = record.channelsDetails?.length
          ? record.channelsDetails.map((channel) => channel.name || channel.type || channel.key).filter(Boolean)
          : [];
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {channels.length > 0 ? channels.join(", ") : "-"}
          </Text>
        );
      },
    },
    {
      title: "Risk score",
      dataIndex: ["risk", "score"],
      key: "risk",
      render: (v: number) => <Progress percent={v || 0} size="small" strokeColor={v > 65 ? "#ff4d4f" : "#36cfc9"} />,
    },
    {
      title: "Alerte",
      key: "alertLevel",
      width: 100,
      render: (_: any, record: Retailer) => {
        const highPriorityProject = record.projects?.find(p => p.priority === 'high' && p.status === 'open');
        if (highPriorityProject) {
          return <Tag color="#fee2e2" style={{ color: '#ef4444', border: '1px solid #fecaca', fontWeight: 600 }} icon={<ThunderboltOutlined />}>CRITIQUE</Tag>;
        }
        const mediumPriorityProject = record.projects?.find(p => p.priority === 'medium' && p.status === 'open');
        if (mediumPriorityProject) {
          return <Tag color="#fffbeb" style={{ color: '#d97706', border: '1px solid #fef3c7', fontWeight: 600 }}>À SUIVRE</Tag>;
        }
        return <Tag color="#f0fdf4" style={{ color: '#16a34a', border: '1px solid #dcfce7', fontWeight: 600 }}>RAS</Tag>;
      }
    },
    {
      title: "Emails",
      key: "alerts",
      width: 100,
      render: (_: any, record: Retailer) => {
        const id = record.id || record._id || "";
        const alert = mailAlerts[id];
        if (!alert || alert.count === 0) {
          return null;
        }
        return (
          <Tooltip title={renderAlertTooltip(alert)} color="white" overlayInnerStyle={{ padding: 0 }}>
            <div className="mail-alert-pill">
              <MessageOutlined style={{ color: '#0ea5e9' }} />
              <span className="count">{alert.count}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button type="text" icon={<AimOutlined />} onClick={() => { setSelected(record); setDrawerOpen(true); }}>Détails</Button>
          <Button type="text" onClick={() => { setSelected(record); editForm.setFieldsValue(toFormValues(record)); setEditDrawerOpen(true); }}>Éditer</Button>
        </Space>
      ),
    },
  ];

  const createChannelKeyOptions = useMemo(() => {
    const details = createForm.getFieldValue("channelsDetails") || [];
    return (details as Array<{ key?: string; name?: string }>).map((channel, index) => ({
      value: channel.key || `channel-${index + 1}`,
      label: channel.key || channel.name || `Canal ${index + 1}`,
    }));
  }, [createForm]);

  return (
    <div className="retailer-cockpit">
      <div className="display-flex  !my-0">
        <div>
          <Title level={2}>Distributeurs</Title>
          <Text type="secondary" style={{ marginBottom: 24, display: "block" }}>
            Pilotage des canaux, de la performance et de la couverture retail.
          </Text>
        </div>
        <Space>
          <Button icon={<DownloadOutlined />} onClick={() => setExportOpen(true)}>
            Exporter CSV
          </Button>
          <Button type="primary" icon={<RocketOutlined />} onClick={() => setCreateDrawerOpen(true)}>Nouveau distributeur</Button>
        </Space>
      </div>

      <Tabs 
        activeKey={activeMainTab} 
        onChange={setactiveMainTab}
        className="neo-main-tabs"
        tabBarExtraContent={
          <Tooltip title="Ajouter un CRM">
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAddCrmTab}
              style={{ marginLeft: 8 }}
            />
          </Tooltip>
        }
        items={[
          {
            key: "network",
            label: (
              <span>
                <ShopOutlined /> Réseau Actif
              </span>
            ),
          },
          ...crmTabs.map((tab) => ({
            key: `crm:${tab.key}`,
            label: (
              <span
                className="crm-tab-label"
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  beginCrmTabRename(tab);
                }}
              >
                {React.createElement(getCrmIconComponent(tab.icon))}
                <span>{tab.label}</span>
                <Tooltip title="Renommer">
                  <EditOutlined
                    className="crm-tab-edit"
                    onClick={(event) => {
                      event.stopPropagation();
                      beginCrmTabRename(tab);
                    }}
                  />
                </Tooltip>
              </span>
            ),
          })),
        ]}
      />

      <Modal
        title="Configurer le CRM"
        open={renameCrmTabOpen}
        width={680}
        onCancel={() => {
          setRenameCrmTabOpen(false);
          setEditingCrmTabKey(null);
        }}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button danger icon={<DeleteOutlined />} onClick={handleDeleteCrm}>
              Supprimer le CRM
            </Button>
            <Space>
              <Button onClick={() => setRenameCrmTabOpen(false)}>Annuler</Button>
              <Button type="primary" onClick={commitCrmTabRename}>
                Enregistrer
              </Button>
            </Space>
          </div>
        }
      >
        <Form layout="vertical">
          <Form.Item label="Nom du tab CRM">
            <Input
              autoFocus
              value={editingCrmTabLabel}
              onChange={(event) => setEditingCrmTabLabel(event.target.value)}
              onPressEnter={commitCrmTabRename}
              placeholder="Ex: Boutiques"
            />
          </Form.Item>
          <Form.Item label="Icône">
            <Select
              value={editingCrmTabIcon}
              onChange={setEditingCrmTabIcon}
              options={CRM_ICON_OPTIONS.map(({ value, label, Icon }) => ({
                value,
                label: (
                  <Space size={8}>
                    <Icon />
                    <span>{label}</span>
                  </Space>
                ),
              }))}
            />
          </Form.Item>
          <Form.Item label="Validateur des prospects">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Aucun validateur requis"
              value={editingCrmValidatorUserId}
              onChange={setEditingCrmValidatorUserId}
              options={accountManagers.map((user) => ({
                value: user._id,
                label: [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || user._id,
              }))}
            />
          </Form.Item>
          <Form.Item label="Colonnes">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {editingCrmColumns.map((column, index) => (
                <div
                  key={column.key}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "42px minmax(0, 1fr) 36px",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <input
                    type="color"
                    aria-label={`Couleur de ${column.label}`}
                    value={column.color}
                    onChange={(event) =>
                      setEditingCrmColumns((previous) =>
                        previous.map((item) =>
                          item.key === column.key ? { ...item, color: event.target.value } : item
                        )
                      )
                    }
                    style={{ width: 38, height: 32, padding: 2, border: "1px solid #d9d9d9", borderRadius: 6 }}
                  />
                  <Input
                    value={column.label}
                    onChange={(event) =>
                      setEditingCrmColumns((previous) =>
                        previous.map((item) =>
                          item.key === column.key ? { ...item, label: event.target.value } : item
                        )
                      )
                    }
                    placeholder={`Colonne ${index + 1}`}
                  />
                  <Tooltip title="Retirer la colonne">
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={editingCrmColumns.length === 1}
                      onClick={() =>
                        setEditingCrmColumns((previous) =>
                          previous.filter((item) => item.key !== column.key)
                        )
                      }
                    />
                  </Tooltip>
                </div>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddCrmColumn}>
                Ajouter une colonne
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Nouveau CRM"
        open={createCrmTabOpen}
        okText="Créer"
        cancelText="Annuler"
        onOk={handleCreateCrmTab}
        onCancel={() => setCreateCrmTabOpen(false)}
      >
        <Form layout="vertical">
          <Form.Item label="Nom du tab CRM">
            <Input
              autoFocus
              value={createCrmTabName}
              onChange={(event) => setCreateCrmTabName(event.target.value)}
              onPressEnter={handleCreateCrmTab}
              placeholder="Ex: Hôtel"
            />
          </Form.Item>
        </Form>
      </Modal>

      <div className="tab-content-wrapper">
        {activeMainTab === "network" ? (
          <>
            <div className="retailer-hero">
              <div className="neo-card map-card">
                <div className="map-header">
                  <div className="map-title">
                    <Title level={4}>Cartographie des canaux</Title>
                    <Text type="secondary">Filtres temps réel par canal, pays et distributeur</Text>
                  </div>
                  <div className="map-filters">
                    <Select size="large" style={{ width: "12rem" }} value={mapChannel} onChange={setMapChannel} options={channelOptions} placeholder="Canal" />
                    <Select size="large" style={{ width: "12rem" }} value={mapCountry} onChange={setMapCountry} options={countryOptions} showSearch placeholder="Pays" />
                    <Select size="large" style={{ width: "12rem" }} value={mapRetailer} onChange={setMapRetailer} options={retailerOptions} showSearch placeholder="Distributeur" />
                  </div>
                </div>

                <div className="europe-map map-rect" ref={mapRef}>
                  <ComposableMap projection="geoMercator" projectionConfig={{ scale: 740, center: [2.3522, 48.8566] }}>
                    <ZoomableGroup minZoom={1.5} maxZoom={25} zoom={mapZoom} center={mapCenter} onMoveEnd={handleMapMoveEnd}>
                      <Geographies geography={EUROPE_GEO_URL}>
                        {({ geographies }: { geographies: any[] }) => geographies
                          .filter(geo => EUROPE_COUNTRIES.has(geo.properties?.name))
                          .filter(geo => mapCountry === "all" || normalizeKey(geo.properties?.name) === normalizeKey(mapCountry))
                          .map(geo => {
                            const key = normalizeKey(geo.properties?.name);
                            const onlineCount = key ? mapData.countryIntensity.get(key) || 0 : 0;
                            const strength = onlineCount ? Math.min(0.9, 0.2 + (onlineCount / mapIntensity.maxCountry) * 0.6) : 0;
                            const stats = key ? mapData.countryStats.get(key) : null;
                            return (
                              <Geography
                                key={geo.rsmKey}
                                geography={geo}
                                fill={onlineCount ? `rgba(56, 189, 248, ${strength})` : (key && mapData.conqueredCountries.has(key) ? "#1e293b" : "#111827")}
                                stroke="#334155"
                                strokeWidth={0.5}
                                style={{ 
                                  default: { outline: "none" }, 
                                  hover: { fill: "#1e293b", stroke: "#38bdf8", outline: "none" } 
                                }}
                                onMouseEnter={(e: React.MouseEvent) => stats && showTooltip(`${geo.properties.name}\nCA: ${formatCurrency(stats.revenue)}\nDist.: ${Array.from(stats.distributors).join(", ")}`, e)}
                                onMouseMove={updateTooltipPosition}
                                onMouseLeave={() => setMapTooltip(p => ({ ...p, visible: false }))}
                              />
                            );
                          })
                        }
                      </Geographies>

                      {mapData.markers.map((marker, i) => (
                        <Marker key={`pos-m-${i}`} coordinates={jitterCoordinates(marker.coordinates, marker.offsetIndex)}>
                          <circle 
                            className={`map-marker ${channelClass(marker.channel)}`} 
                            r={1.2} 
                            style={{ opacity: 1 }} 
                            onMouseEnter={(e: React.MouseEvent) => showTooltip(marker.name, e)} 
                            onMouseMove={updateTooltipPosition} 
                            onMouseLeave={() => setMapTooltip(p => ({ ...p, visible: false }))} 
                          />
                          {mapZoom >= 3.5 && <text className="map-label" textAnchor="middle" y={-10}>{marker.name}</text>}
                        </Marker>
                      ))}
                    </ZoomableGroup>
                  </ComposableMap>
                  {mapTooltip.visible && <div className="map-tooltip" style={{ left: mapTooltip.x + 12, top: mapTooltip.y + 12 }}>{mapTooltip.content}</div>}
                </div>

                <div className="map-kpi-strip">
                  <div className="map-kpi-item">
                    <AimOutlined className="map-kpi-icon" />
                    <div>
                      <div className="map-kpi-value">{mapStats.points}</div>
                      <div className="map-kpi-label">Points actifs</div>
                    </div>
                  </div>
                  <div className="map-kpi-item">
                    <EnvironmentOutlined className="map-kpi-icon" />
                    <div>
                      <div className="map-kpi-value">{mapStats.countries}</div>
                      <div className="map-kpi-label">Pays couverts</div>
                    </div>
                  </div>
                  <div className="map-kpi-item">
                    <RadarChartOutlined className="map-kpi-icon" />
                    <div>
                      <div className="map-kpi-value">{mapStats.posChannels}</div>
                      <div className="map-kpi-label">POS actifs</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
              <Col xs={24} lg={8}>
                <Card className="neo-card" styles={{ body: { padding: '24px' } }}>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Tooltip title="Chiffre d'affaires des commandes retailers (brut) depuis début d'année">
                        <Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sell-in YTD</Text>
                      </Tooltip>
                      <TeamOutlined style={{ color: '#64748b' }} />
                    </div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800 }}>
                      {formatCurrency(globalRetailerKpis.totalAmount)}
                    </Title>
                    <div className="trend-up">
                      {globalRetailerKpis.orderCount} commandes YTD
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card className="neo-card" styles={{ body: { padding: '24px' } }}>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Tooltip title="Revenu net (sell-in - rétrocession - coûts import/transport)">
                        <Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sell-out consolidé</Text>
                      </Tooltip>
                      <ThunderboltOutlined style={{ color: '#64748b' }} />
                    </div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800 }}>
                      {formatCurrency(globalRetailerKpis.netRevenue)}
                    </Title>
                    <div className="trend-up">
                      {globalRetailerKpis.activeRetailers} retailers actifs
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card className="neo-card" styles={{ body: { padding: '24px' } }}>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Tooltip title="OTIF : livraisons à l'heure / livraisons réalisées">
                        <Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Qualité exécution</Text>
                      </Tooltip>
                      <SafetyOutlined style={{ color: '#64748b' }} />
                    </div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800 }}>
                      {globalRetailerKpis.onTimeRate.toFixed(1)}%
                    </Title>
                    <div className="trend-up">
                      {globalRetailerKpis.deliveredCount} livraisons
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>

            <Card className="neo-card" style={{ marginBottom: 24 }}>
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} lg={8}>
                  <Input
                    placeholder="Rechercher un distributeur"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Col>
                <Col xs={24} lg={6}>
                  <Select
                    placeholder="Segment"
                    value={segment}
                    onChange={(value) => setSegment(value)}
                    allowClear
                    style={{ width: "100%" }}
                    options={[
                      { value: "A", label: "Segment A" },
                      { value: "B", label: "Segment B" },
                      { value: "C", label: "Segment C" },
                    ]}
                  />
                </Col>
                <Col xs={24} lg={10}>
                  <Space wrap>
                    <Tag color="cyan">Canal retail</Tag>
                    <Tag color="purple">Marketplaces</Tag>
                    <Tag color="gold">Dept stores</Tag>
                    <Tag color="geekblue">E-commerce hubs</Tag>
                  </Space>
                </Col>
              </Row>
            </Card>

            <Card className="neo-card">
              <Table
                rowKey={(record) => record.id || record._id || record.name}
                columns={columns}
                dataSource={filteredRetailers}
                loading={loading}
                pagination={{ pageSize: 6 }}
              />
            </Card>
          </>
        ) : (
          renderCRMSection()
        )}
      </div>

      <Drawer
        title={
          <Space>
            <span>{selected?.name || "Distributeur"}</span>
            {!!selected && (mailAlerts[selected.id || selected._id || ""]?.count || 0) > 0 && (
              <Tooltip title={renderAlertTooltip(mailAlerts[selected.id || selected._id || ""])}>
                <Badge count={mailAlerts[selected.id || selected._id || ""]?.count} />
              </Tooltip>
            )}
          </Space>
        }
        width={1000}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <Space>
            {selected?.crm?.onboardingStage === "launched" && (
              <Button
                onClick={() => {
                  if (!selected) return;
                  editForm.setFieldsValue(toFormValues(selected));
                  setEditDrawerOpen(true);
                }}
              >
                Éditer
              </Button>
            )}
            <Button danger onClick={handleDeleteRetailer}>
              Supprimer
            </Button>
          </Space>
        }
      >
        {selected && (
          selected.crm?.onboardingStage !== "launched" ? renderOnboardingDrawer(selected) : renderPostOnboardingDrawer(selected)
        )}
      </Drawer>

      <Drawer
        title="Modifier distributeur"
        width={560}
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        className="premium-drawer"
        footer={
          <div className="sticky-footer">
            <Button onClick={() => setEditDrawerOpen(false)} className="premium-input">
              Annuler
            </Button>
            <Button type="primary" onClick={() => editForm.submit()} loading={savingEdit} className="premium-input" style={{ background: '#0f172a', borderColor: '#0f172a' }}>
              Enregistrer les modifications
            </Button>
          </div>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          initialValues={{ segment: "C", status: "prospect" }}
          onFinish={handleUpdateRetailer}
          requiredMark={false}
        >
          <Collapse defaultActiveKey={["general", "channels", "logistics"]} ghost expandIconPosition="end">
            <Collapse.Panel
              key="general"
              header={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <Space size={8}>
                    <InfoCircleOutlined />
                    <Typography.Text>Informations Générales</Typography.Text>
                  </Space>
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const name = getFieldValue("name") || "";
                      const segmentValue = getFieldValue("segment") || "C";
                      const statusValue = getFieldValue("status") || "prospect";
                      const summary = [name, `Segment ${segmentValue}`, statusValue].filter(Boolean).join(" • ");
                      return (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {summary || "—"}
                        </Typography.Text>
                      );
                    }}
                  </Form.Item>
                </div>
              }
            >
              <div className="premium-form-section">
                <Form.Item
                  label="Nom du distributeur"
                  name="name"
                  rules={[{ required: true, message: "Nom requis" }]}
                >
                  <Input placeholder="Ex: Galeries & Partners" className="premium-input" />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Segment" name="segment">
                      <Select
                        className="premium-select"
                        options={[
                          { value: "A", label: "Segment A" },
                          { value: "B", label: "Segment B" },
                          { value: "C", label: "Segment C" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Statut" name="status">
                      <Select
                        className="premium-select"
                        options={[
                          { value: "active", label: "Actif" },
                          { value: "prospect", label: "Prospect" },
                          { value: "paused", label: "Pause" },
                          { value: "dormant", label: "Dormant" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </Collapse.Panel>

            <Collapse.Panel
              key="channels"
              header={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <Space size={8}>
                    <ShopOutlined />
                    <Typography.Text>Canaux de distribution</Typography.Text>
                  </Space>
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const details = (getFieldValue("channelsDetails") || []) as Array<{ type?: string; name?: string }>;
                      const types = Array.from(new Set(details.map((channel) => channel.type).filter(Boolean)));
                      const summary = `${details.length || 0} canal${details.length > 1 ? "x" : ""}${types.length ? ` • ${types.join(", ")}` : ""}`;
                      return (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {summary}
                        </Typography.Text>
                      );
                    }}
                  </Form.Item>
                </div>
              }
            >
              <div className="premium-form-section">
                <Form.List name="channelsDetails">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card
                          key={key}
                          className="premium-card"
                          title={
                            <Space>
                              <Badge status="processing" color="#3b82f6" />
                              <span>Canal {name + 1}</span>
                            </Space>
                          }
                          extra={
                            <Space>
                              <Form.Item noStyle shouldUpdate>
                                {({ getFieldValue }) => {
                                  const type = getFieldValue(["channelsDetails", name, "type"]);
                                  return type ? <Tag color="blue" style={{ borderRadius: 6, margin: 0 }}>{type}</Tag> : null;
                                }}
                              </Form.Item>
                              <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />} 
                                onClick={() => remove(name)}
                                size="small"
                              />
                            </Space>
                          }
                        >
                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, "name"]} label="Nom du canal">
                                <Input placeholder="Flagship Haussmann" className="premium-input" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, "key"]} label="ID Technique">
                                <Input placeholder="printemps-pos-haussmann" className="premium-input" />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item
                                {...restField}
                                name={[name, "type"]}
                                label="Type de canal"
                                rules={[{ required: true, message: "Type requis" }]}
                              >
                                <Select
                                  className="premium-select"
                                  options={[
                                    { value: "pos", label: "POS (boutique)" },
                                    { value: "ecom", label: "Ecom" },
                                    { value: "marketplace", label: "Marketplace" },
                                    { value: "department_store", label: "Department store" },
                                    { value: "wholesale", label: "Wholesale" },
                                  ]}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, "status"]} label="Statut">
                                <Select
                                  className="premium-select"
                                  options={[
                                    { value: "planned", label: "Planned" },
                                    { value: "active", label: "Active" },
                                    { value: "paused", label: "Paused" },
                                  ]}
                                />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, "launchedAt"]} label="Lancement">
                                <DatePicker style={{ width: "100%" }} className="premium-input" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, "stoppedAt"]} label="Fin">
                                <DatePicker style={{ width: "100%" }} className="premium-input" />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                              const type = getFieldValue(["channelsDetails", name, "type"]);
                              if (type !== "pos" && type !== "department_store") return null;
                              return (
                                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
                                  <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 12, color: '#64748b' }}>
                                    LOCALISATION
                                  </Typography.Text>
                                  <Form.Item {...restField} name={[name, "location", "street"]} label="Adresse">
                                    <Input placeholder="64 Boulevard Haussmann" className="premium-input" />
                                  </Form.Item>
                                  <Row gutter={12}>
                                    <Col span={10}>
                                      <Form.Item {...restField} name={[name, "location", "city"]} label="Ville">
                                        <Input placeholder="Paris" className="premium-input" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                      <Form.Item {...restField} name={[name, "location", "postalCode"]} label="CP">
                                        <Input className="premium-input" />
                                      </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                      <Form.Item {...restField} name={[name, "location", "country"]} label="Pays">
                                        <Select
                                          className="premium-select"
                                          options={EUROPE_COUNTRY_OPTIONS}
                                          showSearch
                                        />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </div>
                              );
                            }}
                          </Form.Item>
                        </Card>
                      ))}
                      <Button 
                        type="dashed" 
                        onClick={() => add()} 
                        block 
                        icon={<PlusOutlined />}
                        className="channel-ghost-button"
                      >
                        Ajouter un canal de vente
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </Collapse.Panel>

            <Collapse.Panel
              key="logistics"
              header={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <Space size={8}>
                    <EnvironmentOutlined />
                    <Typography.Text>Configuration Logistique</Typography.Text>
                  </Space>
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const mode = getFieldValue(["logistics", "fulfillmentMode"]);
                      const label = mode === "multi_hub" ? "Multi-hubs" : mode === "per_channel" ? "Par canal" : "Hub unique";
                      return (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {label}
                        </Typography.Text>
                      );
                    }}
                  </Form.Item>
                </div>
              }
            >
              <div className="premium-form-section">
                <Form.Item name={["logistics", "fulfillmentMode"]} label="Stratégie de fulfillment">
                  <Select
                    className="premium-select"
                    options={[
                      { value: "hub", label: "Hub unique" },
                      { value: "multi_hub", label: "Multi-hubs" },
                      { value: "per_channel", label: "Par canal" },
                    ]}
                  />
                </Form.Item>

                {editFulfillmentMode === "hub" && (
                  <Card className="premium-card" style={{ background: "#f8fafc" }}>
                    <Form.Item name={["logistics", "hub", "label"]} label="Libellé du Hub">
                      <Input placeholder="Hub central" className="premium-input" />
                    </Form.Item>
                    <Form.Item name={["logistics", "hub", "street"]} label="Adresse">
                      <Input placeholder="12 Rue du Hub" className="premium-input" />
                    </Form.Item>
                    <Row gutter={12}>
                      <Col span={10}>
                        <Form.Item name={["logistics", "hub", "city"]} label="Ville">
                          <Input placeholder="Paris" className="premium-input" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name={["logistics", "hub", "postalCode"]} label="CP">
                          <Input className="premium-input" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name={["logistics", "hub", "country"]} label="Pays">
                          <Select className="premium-select" options={EUROPE_COUNTRY_OPTIONS} showSearch />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                )}

                {editFulfillmentMode === "multi_hub" && (
                  <Form.List name={["logistics", "hubs"]}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Card 
                            key={key} 
                            className="premium-card" 
                            size="small"
                            extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />}
                          >
                            <Form.Item {...restField} name={[name, "label"]} label="Libellé Hub">
                              <Input placeholder="HUB Nord" className="premium-input" />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, "address", "street"]} label="Adresse">
                              <Input className="premium-input" />
                            </Form.Item>
                            <Row gutter={8}>
                              <Col span={12}>
                                <Form.Item {...restField} name={[name, "address", "city"]} label="Ville">
                                  <Input className="premium-input" />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item {...restField} name={[name, "address", "country"]} label="Pays">
                                  <Select className="premium-select" options={EUROPE_COUNTRY_OPTIONS} showSearch />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Card>
                        ))}
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} className="channel-ghost-button">
                          Ajouter un Hub logistique
                        </Button>
                      </>
                    )}
                  </Form.List>
                )}
              </div>
            </Collapse.Panel>

            <Collapse.Panel
              key="finance"
              header={
                <Space size={8}>
                  <DollarOutlined />
                  <Typography.Text>Conditions de paiement</Typography.Text>
                </Space>
              }
            >
              <div className="premium-form-section">
                <Form.Item
                  name={["finance", "paymentTermsPolicy", "baseDate"]}
                  label="Événement de départ"
                  initialValue="reception"
                >
                  <Select
                    options={[
                      { value: "reception", label: "Date de réception marchandise" },
                      { value: "order", label: "Date de commande" },
                      { value: "invoice", label: "Date d'émission de facture" },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  name={["finance", "paymentTermsPolicy", "endOfMonth"]}
                  label="Reporter à la fin du mois"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  name={["finance", "paymentTermsPolicy", "extraDays"]}
                  label="Différé supplémentaire"
                  initialValue={0}
                >
                  <InputNumber min={0} addonAfter="jours" style={{ width: "100%" }} />
                </Form.Item>
              </div>
            </Collapse.Panel>

            <Collapse.Panel
              key="notes"
              header={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <Space size={8}>
                    <FileTextOutlined />
                    <Typography.Text>Notes Stratégiques</Typography.Text>
                  </Space>
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const notes = getFieldValue("notes") || "";
                      const preview = notes.length > 60 ? `${notes.slice(0, 60)}…` : notes;
                      return (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {preview || "—"}
                        </Typography.Text>
                      );
                    }}
                  </Form.Item>
                </div>
              }
            >
              <div className="premium-form-section" style={{ marginBottom: 100 }}>
                <Form.Item name="notes">
                  <Input.TextArea 
                    rows={4} 
                    placeholder="Positionnement, contraintes, opportunités..." 
                    className="premium-input"
                    style={{ borderRadius: 12 }}
                  />
                </Form.Item>
              </div>
            </Collapse.Panel>
          </Collapse>
        </Form>
      </Drawer>

      <Drawer
        title={createFlow ? "Nouveau distributeur" : "Nouveau distributeur : onboarding"}
        width={1000}
        open={createDrawerOpen}
        onClose={() => {
          setCreateDrawerOpen(false);
          setCreateFlow(null);
          createForm.resetFields();
          createOnboardingForm.resetFields();
          setDraftContacts([]);
          setContactDrafts({});
          setContactEditing({});
          setNewContactDraft({
            name: "",
            role: "",
            email: "",
            phone: "",
            isDecisionMaker: false,
            contactType: "general",
          });
        }}
      >
        {!createFlow && (
          <Space direction="vertical" style={{ width: "100%" }} size={16}>
            <Title level={5} style={{ margin: 0 }}>Ce distributeur est-il déjà onboardé ?</Title>
            <div className="onboarding-choice-grid">
              <Card
                className={`onboarding-choice-card ${createFlow === "not_onboarded" ? "is-active" : ""}`}
                onClick={() => setCreateFlow("not_onboarded")}
              >
                <div className="choice-card-top">
                  <ThunderboltOutlined />
                  <Text strong>Non, onboarding à faire</Text>
                </div>
                <Text type="secondary">Checklist + KYC + contacts + account manager</Text>
                <div className="choice-card-footer">Commencer l'onboarding</div>
              </Card>
              <Card
                className={`onboarding-choice-card ${createFlow === "onboarded" ? "is-active" : ""}`}
                onClick={() => setCreateFlow("onboarded")}
              >
                <div className="choice-card-top">
                  <CheckCircleOutlined />
                  <Text strong>Oui, déjà onboardé</Text>
                </div>
                <Text type="secondary">Fiche complète (canaux + logistique)</Text>
                <div className="choice-card-footer">Créer la fiche complète</div>
              </Card>
            </div>
          </Space>
        )}

        {createFlow === "not_onboarded" && (
          <Form
            form={createOnboardingForm}
            layout="vertical"
            initialValues={{ segment: "C", kyc: { marketScopeType: "cities" } }}
            onFinish={handleCreateOnboardingRetailer}
          >
            {renderOnboardingDrawer(createOnboardingDraftRetailer)}
            <Space>
              <Button
                onClick={() => {
                  setCreateFlow(null);
                  createOnboardingForm.resetFields();
                  setDraftContacts([]);
                  setContactDrafts({});
                  setContactEditing({});
                  setNewContactDraft({
                    name: "",
                    role: "",
                    email: "",
                    phone: "",
                    isDecisionMaker: false,
                    contactType: "general",
                  });
                }}
              >
                Retour
              </Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                Créer
              </Button>
            </Space>
          </Form>
        )}

        {createFlow === "onboarded" && (
          <Form
            form={createForm}
            layout="vertical"
            initialValues={{ segment: "C", status: "prospect" }}
            onFinish={handleCreateRetailer}
          >
            <Space direction="vertical" style={{ width: "100%" }} size={16}>
              <Space align="center">
                <Button onClick={() => setCreateFlow(null)}>Retour</Button>
                <Text type="secondary">Création distributeur déjà onboardé</Text>
              </Space>
            </Space>
            <Form.Item
              label="Nom du distributeur"
              name="name"
              rules={[{ required: true, message: "Nom requis" }]}
            >
              <Input placeholder="Ex: Galeries & Partners" />
            </Form.Item>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item label="Segment" name="segment">
                  <Select
                    options={[
                      { value: "A", label: "Segment A" },
                      { value: "B", label: "Segment B" },
                      { value: "C", label: "Segment C" },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Statut" name="status">
                  <Select
                    options={[
                      { value: "active", label: "Actif" },
                      { value: "prospect", label: "Prospect" },
                      { value: "paused", label: "Pause" },
                      { value: "dormant", label: "Dormant" },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider>Canaux détaillés</Divider>
            <Form.List name="channelsDetails">
              {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: "100%" }}>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      className="glass-card"
                      style={{ background: "#fbfcff" }}
                      title={`Canal ${name + 1}`}
                      extra={
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => {
                            const type = getFieldValue(["channelsDetails", name, "type"]);
                            return type ? <Tag className={`channel-tag ${type}`}>{type}</Tag> : null;
                          }}
                        </Form.Item>
                      }
                    >
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "key"]} label="Channel key">
                            <Input placeholder="printemps-pos-haussmann" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "name"]} label="Nom canal">
                            <Input placeholder="Flagship Haussmann" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, "type"]}
                            label="Canal"
                            rules={[{ required: true, message: "Type requis" }]}
                          >
                            <Select
                              options={[
                                { value: "pos", label: "POS (boutique)" },
                                { value: "ecom", label: "Ecom" },
                                { value: "marketplace", label: "Marketplace" },
                                { value: "department_store", label: "Department store" },
                                { value: "wholesale", label: "Wholesale" },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "status"]} label="Statut">
                            <Select
                              options={[
                                { value: "planned", label: "Planned" },
                                { value: "active", label: "Active" },
                                { value: "paused", label: "Paused" },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "launchedAt"]} label="Lancement">
                            <DatePicker style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "stoppedAt"]} label="Fin">
                            <DatePicker style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item {...restField} name={[name, "coverage", "countries"]} label="Pays">
                        <Select
                          mode="multiple"
                          placeholder="France, Allemagne..."
                          options={EUROPE_COUNTRY_OPTIONS}
                          showSearch
                        />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "coverage", "regions"]} label="Régions">
                        <Select mode="tags" placeholder="Île-de-France, PACA..." />
                      </Form.Item>
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "coverage", "doors"]} label="Nombre de portes">
                            <Input type="number" min={0} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "notes"]} label="Notes">
                            <Input placeholder="Flagship, roll-out..." />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                          const type = getFieldValue(["channelsDetails", name, "type"]);
                          if (type !== "pos" && type !== "department_store") return null;
                          return (
                            <>
                              <Divider>Localisation (POS / Department store)</Divider>
                              <Row gutter={12}>
                                <Col span={12}>
                                  <Form.Item {...restField} name={[name, "location", "label"]} label="Nom boutique">
                                    <Input placeholder="Printemps Haussmann" />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item {...restField} name={[name, "location", "street"]} label="Adresse">
                                    <Input placeholder="64 Boulevard Haussmann" />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Row gutter={12}>
                                <Col span={8}>
                                  <Form.Item {...restField} name={[name, "location", "city"]} label="Ville">
                                    <Input placeholder="Paris" />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item {...restField} name={[name, "location", "postalCode"]} label="Code postal">
                                    <Input />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item {...restField} name={[name, "location", "country"]} label="Pays">
                                    <Select
                                      placeholder="France"
                                      options={EUROPE_COUNTRY_OPTIONS}
                                      showSearch
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </>
                          );
                        }}
                      </Form.Item>
                      {createFulfillmentMode === "per_channel" && (
                        <>
                          <Divider>Logistique du canal</Divider>
                          <Form.Item
                            {...restField}
                            name={[name, "logistics", "fulfillmentMode"]}
                            label="Mode logistique"
                          >
                            <Select
                              options={[
                                { value: "hub", label: "Hub" },
                                { value: "direct_store", label: "Direct magasin" },
                              ]}
                            />
                          </Form.Item>
                          <Row gutter={12}>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, "logistics", "hub", "label"]} label="Hub">
                                <Input placeholder="Hub central" />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, "logistics", "hub", "street"]} label="Adresse hub">
                                <Input placeholder="12 Rue du Hub" />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={12}>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, "logistics", "hub", "city"]} label="Ville hub">
                                <Input placeholder="Paris" />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                {...restField}
                                name={[name, "logistics", "hub", "postalCode"]}
                                label="Code postal"
                              >
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, "logistics", "hub", "country"]} label="Pays">
                                <Select options={EUROPE_COUNTRY_OPTIONS} showSearch />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )}
                      <Button danger style={{ marginTop: 8 }} onClick={() => remove(name)}>
                        Supprimer le canal
                      </Button>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={() => add()}>
                    Ajouter un canal
                  </Button>
                </Space>
              )}
            </Form.List>
            <Divider>Logistique</Divider>
            <Form.Item name={["logistics", "fulfillmentMode"]} label="Mode de fulfillment">
              <Select
                options={[
                  { value: "hub", label: "Hub unique" },
                  { value: "multi_hub", label: "Multi-hubs" },
                  { value: "per_channel", label: "Par canal" },
                ]}
              />
            </Form.Item>
            {createFulfillmentMode === "hub" && (
              <Card className="glass-card" style={{ background: "#fbfcff", marginBottom: 16 }}>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name={["logistics", "hub", "label"]} label="Hub principal">
                      <Input placeholder="Hub central" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={["logistics", "hub", "street"]} label="Adresse hub">
                      <Input placeholder="12 Rue du Hub" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item name={["logistics", "hub", "city"]} label="Ville">
                      <Input placeholder="Paris" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={["logistics", "hub", "postalCode"]} label="Code postal">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={["logistics", "hub", "country"]} label="Pays">
                      <Select options={EUROPE_COUNTRY_OPTIONS} showSearch />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            )}
            {createFulfillmentMode === "multi_hub" && (
              <Form.List name={["logistics", "hubs"]}>
                {(fields, { add, remove }) => (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row gutter={8} key={key}>
                        <Col span={8}>
                          <Form.Item {...restField} name={[name, "label"]} label="Hub">
                            <Input placeholder="HUB Nord" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item {...restField} name={[name, "address", "street"]} label="Adresse">
                            <Input placeholder="12 Rue du Hub" />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item {...restField} name={[name, "address", "city"]} label="Ville">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item {...restField} name={[name, "address", "postalCode"]} label="CP">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item {...restField} name={[name, "address", "country"]} label="Pays">
                            <Select options={EUROPE_COUNTRY_OPTIONS} showSearch />
                          </Form.Item>
                        </Col>
                        <Col span={4} style={{ display: "flex", alignItems: "center" }}>
                          <Button danger onClick={() => remove(name)}>Retirer</Button>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            {...restField}
                            name={[name, "channelKeys"]}
                            label="Canaux rattachés (channel key)"
                          >
                            <Select
                              mode="multiple"
                              options={createChannelKeyOptions}
                              placeholder="printemps-pos-haussmann"
                              showSearch
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                    <Button onClick={() => add()}>Ajouter un hub</Button>
                  </Space>
                )}
              </Form.List>
            )}
            <Form.Item label="Notes stratégiques" name="notes">
              <Input.TextArea rows={4} placeholder="Positionnement, contraintes, opportunités" />
            </Form.Item>
            <Space>
              <Button onClick={() => setCreateDrawerOpen(false)}>Annuler</Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                Créer
              </Button>
            </Space>
          </Form>
        )}
      </Drawer>

      <Modal
        title="Exporter les distributeurs (CSV)"
        open={exportOpen}
        onCancel={() => setExportOpen(false)}
        okText="Télécharger CSV"
        onOk={handleExportCsv}
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Text type="secondary">
            L'export prend en compte les filtres actuels (recherche/segment) et les filtres ci-dessous.
          </Text>
          <Row gutter={12}>
            <Col span={12}>
              <Text type="secondary">Pays</Text>
              <Select
                style={{ width: "100%" }}
                value={exportCountry}
                onChange={setExportCountry}
                options={countryOptions}
                showSearch
              />
            </Col>
            <Col span={12}>
              <Text type="secondary">Distributeurs (cas par cas)</Text>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Tous"
                value={exportRetailersSelection}
                onChange={setExportRetailersSelection}
                options={exportRetailerOptions}
                showSearch
                optionFilterProp="label"
              />
            </Col>
          </Row>
          <Divider style={{ margin: 0 }} />
          <Text type="secondary">Colonnes</Text>
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            dataSource={exportColumns}
            columns={[
              {
                title: "",
                dataIndex: "selected",
                width: 48,
                render: (selected: boolean, record: any) => (
                  <Checkbox
                    checked={selected}
                    onChange={(event) => updateExportColumnSelection(record.key, event.target.checked)}
                  />
                ),
              },
              {
                title: "Donnée",
                dataIndex: "label",
                key: "label",
              },
              {
                title: "Nom de colonne",
                dataIndex: "header",
                key: "header",
                render: (value: string, record: any) => (
                  <Input
                    value={value}
                    onChange={(event) => updateExportColumnHeader(record.key, event.target.value)}
                  />
                ),
              },
            ]}
          />
          <Text type="secondary">
            {exportRetailers.length} distributeur(s) correspondent aux filtres.
          </Text>
        </Space>
      </Modal>

      <style dangerouslySetInnerHTML={{ __html: `
        .retailer-cockpit {
          padding: 24px;
          min-height: 100vh;
          background: #f8fafc;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        .retailer-hero {
          margin-bottom: 32px;
        }
        .display-flex {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 32px;
        }
        .display-flex h2 {
          margin: 0 !important;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: #0f172a;
        }
        .neo-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .neo-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }
        .pipeline-column {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .column-content {
          flex: 1;
        }
        .onboarding-choice-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
        }
        .onboarding-choice-card {
          border-radius: 16px;
          border: 1px solid rgba(148, 163, 184, 0.3);
          background: linear-gradient(140deg, #0f172a 0%, #111827 45%, #0b1220 100%);
          color: #e2e8f0;
          padding: 18px;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
          position: relative;
          overflow: hidden;
        }
        .onboarding-choice-card::after {
          content: "";
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at top right, rgba(56, 189, 248, 0.25), transparent 55%);
          opacity: 0.7;
          pointer-events: none;
        }
        .onboarding-choice-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.25);
          border-color: rgba(56, 189, 248, 0.6);
        }
        .onboarding-choice-card.is-active {
          border-color: #38bdf8;
          box-shadow: 0 0 0 1px rgba(56, 189, 248, 0.5), 0 18px 40px rgba(15, 23, 42, 0.35);
        }
        .onboarding-choice-card .ant-typography {
          color: #e2e8f0;
        }
        .onboarding-choice-card .ant-typography-secondary {
          color: #94a3b8;
        }
        .choice-card-top {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          margin-bottom: 12px;
          z-index: 1;
          position: relative;
        }
        .choice-card-top svg {
          font-size: 18px;
          color: #38bdf8;
          filter: drop-shadow(0 0 6px rgba(56, 189, 248, 0.6));
        }
        .choice-card-footer {
          margin-top: 16px;
          font-size: 12px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #38bdf8;
          font-weight: 600;
          z-index: 1;
          position: relative;
        }
        .premium-drawer .ant-drawer-header {
          border-bottom: 1px solid #f1f5f9;
          padding: 20px 24px;
        }
        .premium-drawer .ant-drawer-title {
          font-weight: 700;
          font-size: 18px;
          letter-spacing: -0.01em;
          color: #0f172a;
        }
        .premium-form-section {
          margin-bottom: 32px;
        }
        .section-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid #f1f5f9;
        }
        .section-header .ant-typography {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
        }
        .premium-card {
          border: 1px solid #f1f5f9 !important;
          border-radius: 12px !important;
          box-shadow: 0 1px 2px rgba(0,0,0,0.03) !important;
          margin-bottom: 16px !important;
          transition: all 0.2s ease !important;
        }
        .premium-card:hover {
          border-color: #e2e8f0 !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
        }
        .premium-card .ant-card-head {
          border-bottom: 1px solid #f1f5f9;
          min-height: 48px;
          padding: 0 16px;
        }
        .premium-card .ant-card-head-title {
          font-size: 14px;
          font-weight: 600;
          color: #334155;
        }
        .premium-input {
          border-radius: 8px !important;
          border-color: #e2e8f0 !important;
        }
        .sticky-footer {
          padding: 16px 24px;
          background: #ffffff;
          border-top: 1px solid #f1f5f9;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .map-card {
          padding: 0 !important;
          overflow: hidden;
          border: none;
          background: #0f172a !important; /* Deep Navy Map Background */
        }
        .map-header {
          padding: 24px;
          background: rgba(15, 23, 42, 0.8);
          backdrop-filter: blur(8px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
          z-index: 10;
        }
        .map-title h4 {
          color: #f8fafc !important;
          margin: 0 !important;
          font-weight: 700;
        }
        .map-title span {
          color: #94a3b8 !important;
        }
        .map-filters {
          display: flex;
          gap: 12px;
        }
        .map-filters .ant-select-selector {
          background: rgba(30, 41, 59, 0.5) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          color: #f8fafc !important;
          border-radius: 8px !important;
        }
        .map-filters .ant-select-selection-placeholder {
          color: #64748b !important;
        }
        .map-filters .ant-select-selection-item {
          color: #f8fafc !important;
        }
        .map-rect {
          height: 480px;
          background: transparent;
          position: relative;
        }
        .europe-map svg {
          width: 100%;
          height: 100%;
          filter: drop-shadow(0 0 20px rgba(0, 0, 0, 0.5));
        }
        .map-marker.pos {
          fill: #38bdf8; /* Cyan/Blue Neon */
          stroke: #ffffff;
          stroke-width: 0.2;
          filter: drop-shadow(0 0 8px rgba(56, 189, 248, 0.6));
        }
        .map-cluster {
          fill: #ffffff;
          stroke: #38bdf8;
          stroke-width: 2;
          filter: drop-shadow(0 0 12px rgba(56, 189, 248, 0.4));
        }
        .map-cluster-label {
          fill: #0f172a;
          font-weight: 800;
          font-size: 10px;
        }
        .map-tooltip {
          position: absolute;
          z-index: 1000;
          background: #1e293b;
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #f8fafc;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 13px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          pointer-events: none;
        }
        .map-kpi-strip {
          display: flex;
          gap: 24px;
          padding: 20px 24px;
          background: rgba(15, 23, 42, 0.9);
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        .map-kpi-item {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .map-kpi-icon {
          font-size: 18px;
          color: #38bdf8;
        }
        .map-kpi-value {
          font-size: 18px;
          font-weight: 700;
          color: #f8fafc;
        }
        .map-kpi-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
        }
        .hero-pill {
          padding: 6px 14px;
          border-radius: 6px;
          background: #f1f5f9;
          color: #475569;
          font-weight: 600;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .hero-pill.accent {
          background: #e0f2fe;
          color: #0369a1;
        }
        .hero-pill.ghost {
          background: transparent;
          border: 1px solid #e2e8f0;
        }
        .trend-up { color: #10b981; font-weight: 600; font-size: 13px; }
        .trend-down { color: #ef4444; font-weight: 600; font-size: 13px; }

        /* Mail Alerts Pill */
        .mail-alert-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          padding: 4px 10px;
          border-radius: 9999px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .mail-alert-pill:hover {
          background: #e0f2fe;
          border-color: #7dd3fc;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }
        .mail-alert-pill .count {
          font-weight: 700;
          color: #0369a1;
          font-size: 13px;
        }

        /* Custom Mail Tooltip */
        .custom-mail-tooltip {
          min-width: 240px;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
        .custom-mail-tooltip .tooltip-header {
          padding: 10px 14px;
          border-bottom: 1px solid #f1f5f9;
          background: #f8fafc;
        }
        .custom-mail-tooltip .tooltip-body {
          padding: 8px 0;
        }
        .custom-mail-tooltip .tooltip-item {
          padding: 8px 14px;
          display: flex;
          gap: 12px;
          align-items: flex-start;
          transition: background 0.2s ease;
        }
        .custom-mail-tooltip .tooltip-item:hover {
          background: #f8fafc;
        }
        .custom-mail-tooltip .item-dot {
          width: 8px;
          height: 8px;
          background: #0ea5e9;
          border-radius: 50%;
          margin-top: 5px;
          flex-shrink: 0;
        }
        .custom-mail-tooltip .tooltip-footer {
          padding: 8px 14px;
          border-top: 1px solid #f1f5f9;
          text-align: center;
          background: #f8fafc;
        }

        /* Projects Tab Styles */
        .projects-tab {
          padding: 16px 0;
        }
        .projects-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        .section-title {
          margin-bottom: 16px !important;
          color: #1e293b !important;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .project-card {
          border-radius: 12px;
          margin-bottom: 16px;
          border-left: 4px solid #e2e8f0;
          transition: transform 0.2s ease;
        }
        .project-card:hover {
          transform: translateY(-2px);
        }
        .project-card.high { border-left-color: #ef4444; }
        .project-card.medium { border-left-color: #f59e0b; }
        .project-card.low { border-left-color: #10b981; }
        
        .project-card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }
        .project-title {
          font-size: 16px;
          color: #0f172a;
        }
        .project-desc {
          display: block;
          margin-bottom: 16px;
          font-size: 13px;
        }
        .project-attached-emails {
          margin-bottom: 12px;
        }
        .attached-email-tag {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #f1f5f9;
          padding: 4px 8px;
          border-radius: 6px;
          margin-bottom: 4px;
          font-size: 11px;
          color: #475569;
        }
        .attached-email-tag .ant-typography {
          max-width: 200px;
        }

        .project-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8fafc;
          padding: 8px 12px;
          border-radius: 8px;
        }

        .email-history-list {
          max-height: 600px;
          overflow-y: auto;
          padding-right: 8px;
        }
        .email-item {
          display: flex;
          gap: 12px;
          padding: 12px;
          border-radius: 10px;
          margin-bottom: 12px;
          background: white;
          border: 1px solid #f1f5f9;
          position: relative;
          transition: all 0.2s ease;
        }
        .email-item:hover {
          border-color: #e2e8f0;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }
        .email-item.unread {
          background: #f0f9ff;
          border-color: #bae6fd;
        }
        .unread-dot {
          width: 8px;
          height: 8px;
          background: #0ea5e9;
          border-radius: 50%;
          position: absolute;
          top: 12px;
          right: 12px;
        }
        .email-content {
          flex: 1;
          min-width: 0;
        }
        .email-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 4px;
        }
        .email-subject {
          font-size: 13px;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-right: 40px;
        }
        .email-date {
          font-size: 11px;
          color: #94a3b8;
          white-space: nowrap;
        }
        .email-from {
          font-size: 12px;
          display: block;
          margin-bottom: 4px;
        }
        .email-preview {
          font-size: 12px;
          display: block;
          line-height: 1.4;
        }

        .empty-projects {
          text-align: center;
          padding: 48px 24px;
          background: #f8fafc;
          border: 2px dashed #e2e8f0;
          border-radius: 12px;
        }

        /* Projects Tab Styles - Premium Redesign */
        .projects-view {
          padding: 12px;
          background: #ffffff;
          border-radius: 24px;
          min-height: calc(100vh - 180px);
        }

        .view-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          background: transparent;
          box-shadow: none;
          padding: 0;
        }

        .header-info-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .premium-search {
          width: 280px;
          border-radius: 12px;
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          height: 40px;
          transition: all 0.2s ease;
        }

        .people-filters {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          max-width: 600px;
        }

        .filter-pill {
          height: 32px;
          padding: 0 12px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          min-width: 36px;
          max-width: 160px;
          background: #ffffff;
          cursor: pointer;
          display: flex;
          align-items: center;
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          transition: all 0.2s ease;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .filter-pill:hover {
          border-color: #3b82f6;
          color: #3b82f6;
          background: #eff6ff;
        }

        .filter-pill.active {
          background: #3b82f6;
          color: #ffffff;
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
        }

        .kanban-column {
          background: #f1f5f9;
          border-radius: 14px;
          height: calc(100vh - 420px);
          min-height: 500px;
          display: flex;
          flex-direction: column;
          padding: 12px;
          border: 1px solid #e2e8f0;
        }

        .operational-project-card {
          border-radius: 10px;
          border: 1.6px solid #0f172a !important;
          margin-bottom: 12px;
          margin-top: 12px;
          background: #ffffff;
          transition: all 0.2s ease;
          cursor: pointer;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }

        .operational-project-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .operational-project-card.active {
          border-color: #3b82f6 !important;
          background: #f0f7ff;
        }

        /* Carousel Styles */
        .premium-carousel {
          display: flex;
          gap: 16px;
          overflow-x: auto;
          padding: 8px 4px 20px 4px;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
        }

        .premium-carousel::-webkit-scrollbar {
          height: 6px;
        }

        .premium-carousel::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }

        .premium-carousel::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }

        .carousel-item-mail {
          flex: 0 0 320px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 16px;
          transition: all 0.2s ease;
        }

        /* Project detail header and chronology */
        .detail-top-info {
          display: flex;
          gap: 24px;
          margin-bottom: 32px;
          padding: 24px;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
        }

        .chronology-anchor {
          display: flex;
          align-items: center;
          gap: 4px;
          background: #ffffff;
          padding: 4px 12px;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          font-size: 11px;
          color: #64748b;
          margin-bottom: 16px;
          width: fit-content;
        }

        .attachment-item-premium {
          background: #f8fafc;
          border: 1px solid #f1f5f9;
          border-radius: 14px;
          padding: 12px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .attachment-item-premium:hover {
          background: #ffffff;
          border-color: #3b82f6;
          transform: translateX(4px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }

        .incoming-mail-card {
          display: flex;
          gap: 20px;
          padding: 20px;
          border-radius: 20px;
          background: #ffffff;
          border: .5px solid #3b82f6;
          margin-bottom: 16px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0px 0px 7px #3b82f6
        }

        .incoming-mail-card:hover {
          transform: translateY(-2px);
          border-color: #3b82f6;
          box-shadow: 0 15px 30px rgba(0,0,0,0.06);
        }

        .mail-sender-avatar {
          width: 48px;
          height: 48px;
          border-radius: 16px;
          font-size: 24px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          box-shadow: 0 4px 10px rgba(30, 41, 59, 0.2);
        }

        .footer-meta {
          color: #94a3b8;
          font-size: 11px;
        }

        /* Timeline Premium */
        .timeline-panel {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .timeline-scroll {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          background: #fafafa;
        }

        .timeline-item-premium {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 12px;
          margin-bottom: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .timeline-item-premium:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }

        .timeline-item-premium.selected {
          border-color: #3b82f6;
          background: #eff6ff;
        }

        .timeline-item-premium.unread {
          border-left: 3px solid #3b82f6;
        }

        .timeline-item-premium .item-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
        }

        .timeline-item-premium .subject {
          font-size: 13px;
          color: #1e293b;
        }

        .timeline-item-premium .date {
          font-size: 11px;
          color: #94a3b8;
        }

        .timeline-item-premium .item-preview {
          font-size: 12px;
          color: #64748b;
          line-height: 1.4;
          margin-bottom: 8px;
          display: block;
        }

        .timeline-item-premium .item-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .priority-dot {
          display: inline-block;
          margin-right: 8px;
        }

        .unassigned-strip {
          margin-top: 16px;
          background: #ffffff;
          border: 1px dashed #e2e8f0;
          border-radius: 12px;
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .strip-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .strip-items {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 10px;
        }

        .strip-item {
          background: #f8fafc;
          border-radius: 10px;
          padding: 8px 10px;
          border: 1px solid #e2e8f0;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .strip-item.selected {
          border-color: #3b82f6;
          background: #eff6ff;
          box-shadow: 0 6px 12px rgba(59, 130, 246, 0.15);
        }

        .strip-title {
          font-size: 12px;
          color: #1e293b;
          font-weight: 600;
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .strip-meta {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: #94a3b8;
        }

        .project-detail-view {
          background: #ffffff;
          border-radius: 16px;
          padding: 16px 20px;
          box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
          border: 1px solid #e2e8f0;
        }

        .detail-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 16px;
        }

        .detail-title {
          flex: 1;
        }

        .detail-meta {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
          margin-bottom: 16px;
        }

        .detail-mail-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 12px;
          max-height: 520px;
          overflow-y: auto;
          padding-right: 6px;
        }

        .project-mail-card {
          background: #ffffff;
          border-radius: 12px;
          padding: 12px;
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }

        .project-mail-card.unread {
          border-left: 3px solid #3b82f6;
          background: #f0f7ff;
        }

        .mail-card-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 8px;
          margin-bottom: 6px;
        }

        .mail-preview {
          display: block;
          margin-bottom: 8px;
          font-size: 12px;
          color: #64748b;
        }

        .mail-card-footer {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          color: #94a3b8;
        }

        
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 40px;
          text-align: center;
        }
        .empty-icon {
          font-size: 48px;
          color: #e2e8f0;
          margin-bottom: 16px;
        }
        .loading-state {
          display: flex;
          justify-content: center;
          padding: 40px;
          color: #0ea5e9;
          font-size: 24px;
        }

        .ant-table {
          background: transparent !important;
        }
        .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #64748b !important;
          font-size: 12px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          border-bottom: 1px solid #e2e8f0 !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
        }
        .ant-btn-primary {
          background: #0f172a !important;
          border: none !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          height: 40px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }
        .ant-btn-primary:hover {
          background: #1e293b !important;
        }

        /* CRM Pipeline Styles */
        .crm-pipeline {
          margin-top: 8px;
        }
        .pipeline-column {
          background: #f8fafc;
          border-radius: 12px;
          min-height: 550px;
          min-width: 0;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }
        .pipeline-column.drag-over {
          background: #f1f5f9;
          border: 2px dashed #3b82f6;
          transform: translateY(-2px);
        }
        .column-header {
          padding: 16px;
          background: white;
          border-radius: 12px 12px 0 0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          min-width: 0;
          gap: 4px;
          margin-bottom: 4px;
        }
        .count-tag {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          margin: 0;
        }
        .column-content {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          flex: 1;
          overflow-y: auto;
        }
        .empty-stage {
          text-align: center;
          color: #94a3b8;
          padding: 24px 0;
          font-size: 13px;
          font-style: italic;
        }
        .crm-card {
          border-radius: 10px;
          margin-top: 8px;
          min-width: 0;
          overflow: hidden;
          border: 1.6px solid #0f172a;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }
        .crm-card:hover {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .crm-card .ant-card-body {
          padding: 16px !important;
          min-width: 0;
          overflow: hidden;
        }
        .card-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }
        .card-middle {
          margin-bottom: 16px;
        }
        .probability-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
        }
        .prob-text {
          font-size: 11px;
          color: #64748b;
          font-weight: 600;
        }
        .card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 12px;
          border-top: 1px solid #f1f5f9;
        }

        .project-checklist-text {
          position: relative;
          display: inline-block;
        }

        .project-checklist-text::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          top: 50%;
          height: 2px;
          background: #94a3b8;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.25s ease;
        }

        .project-checklist-item.is-completed .project-checklist-text::after {
          transform: scaleX(1);
        }
        
        .neo-main-tabs .ant-tabs-nav {
          margin-bottom: 24px !important;
        }
        .neo-main-tabs .ant-tabs-tab {
          padding: 12px 16px !important;
          font-weight: 600 !important;
        }
        .crm-tab-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 24px;
        }
        .crm-tab-edit {
          opacity: 0;
          font-size: 11px;
          transition: opacity 0.18s ease;
        }
        .neo-main-tabs .ant-tabs-tab:hover .crm-tab-edit,
        .neo-main-tabs .ant-tabs-tab-active .crm-tab-edit {
          opacity: 0.7;
        }
        .neo-main-tabs .ant-tabs-tab-active {
          color: #0f172a !important;
        }

        .ant-tabs-tab-btn:hover {
          color: #0f172a70 !important;
        }

        .ant-tabs-tab-btn {
          color: #0f172a70 !important;
        }

        .ant-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #0f172a !important;
        }
        .neo-main-tabs .ant-tabs-ink-bar {
          background: #0f172a !important;
          height: 3px !important;
        }
      ` }} />
    </div>
  );
};

export default Distributeurs;
